import React, { useCallback, useEffect, useMemo, Suspense, useRef, useState } from "react";
import swal from '@sweetalert/with-react';
import { API } from "aws-amplify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay, faLock, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { useReactToPrint } from "react-to-print";

import MagnetboardTopBar from "./components/MagnetboardTopBar";
import MagnetboardMenu from "./components/MagnetboardMenu";
import MagnetboardFilters from "./components/MagnetboardFilters";
import MagnetboardPanel from "./components/MagnetboardPanel";
import MagnetboardPrintSchedule from "./components/MagnetboardPrintSchedule";
import MagnetboardPrintByTeacher from "./components/MagnetboardPrintByTeacher";
import MagnetboardPrintOptionsModal from "./components/MagnetboardPrintOptionsModal";
import MagnetboardExcelExportOptionsModal from "./components/MagnetboardExcelExportOptionsModal";

import { isEmpty, getJSONFromStorage, calculateAffectedSectionsForPlacements, saveSectionPlacement, calculateConflicts, getData, sortArrayOfObjects, sortObject, findCourseRequestInStudentRequests, loadData, cloneObj, clearSectionPlacement, runSectionPlacement, runClassroomPlacement, updateManualPlacement as updateManualPlacementAPI } from "../../../js/Helpers";
import { buildPeriodsToShow, getCoursePeriodIdForPeriod, getPeriodNameFromCoursePeriodId } from "../../../js/PeriodHelpers";
import { FileUpload } from '../../../js/upload'

import {
  useScheduleData,
  useScheduleActions,
  useViewData,
  useViewActions,
  useConflictActions,
  useConflictData,
  useLoadingActions,
  useLoadingData,
  useFilterData,
} from "../store/magnetboardStore";
import { useModalsActions, useModalsData } from "../store/modalsStore";
import { buildSectionSingletonDoubletonMap } from "./utils/sectionClassification";
import { getFilteredDisplaySchedule, sectionPassesSectionFilters } from "./utils/displayFilters";
import { DEFAULT_PRINT_OPTIONS } from "./utils/printOptions";
import { getDiagnosticTimestamp, logMagnetboardDiagnostic, startMagnetboardDiagnosticTimer } from "./utils/diagnostics";
import { buildMagnetboardExcelExport, formatMagnetboardExcelWorksheet, userCanExportMagnetboardExcel } from "./utils/magnetboardExcelExport";
import { NO_DEPARTMENT_FILTER_DISPLAY, NO_DEPARTMENT_FILTER_VALUE, hasNoDepartmentValues } from "../utils/departmentFilterUtils";
import { getSectionTermFilterInfo, getTermFilterOptionsFromSections } from "../utils/sectionTermUtils";
import Loader from "../common/Loader";
import { MAGNETBOARD_PRINT_PAGE_STYLE, usePrintData, useReportsActions } from "../store/reportsStore";
import { CONFLICT_TYPES } from "../../../js/Constants";
import { filterOutConflicts } from "../../../js/Conflicts";
import { filterConflictsForReportScope } from "../reports/utils/conflictScope";

const UNPLACED_FILTER_VALUE = 'unplaced';

const getSelectedFilterValues = (filterOptions = {}, filterKey) => {
  const options = Array.isArray(filterOptions?.[filterKey]) ? filterOptions[filterKey] : [];
  return options.filter(option => option.selected).map(option => `${option.value}`);
};

const sectionMatchesCourseViewPeriodTermFilters = (section, filterOptions = {}) => {
  const selected_periods = getSelectedFilterValues(filterOptions, 'periods');
  const selected_terms = getSelectedFilterValues(filterOptions, 'terms');
  const section_periods = Array.isArray(section?.course_periods) ? section.course_periods.map(period_id => `${period_id}`) : [];

  if(selected_periods.length > 0)
  {
    const matches_period = selected_periods.some(selected_period => (
      selected_period === UNPLACED_FILTER_VALUE
        ? section_periods.length === 0
        : section_periods.includes(selected_period)
    ));
    if(!matches_period) return false;
  }

  if(selected_terms.length > 0)
  {
    const section_term_filter_value = `${getSectionTermFilterInfo(section).value}`;
    if(!selected_terms.some(selected_term => selected_term === section_term_filter_value)) return false;
  }

  return true;
};

const ExportScheduleModal = React.lazy(() => import('../modals/export_schedule'));
const EditTeacherData = React.lazy(() => import('../modals/edit_teacher'));
const EditCourseData = React.lazy(() => import('../modals/edit_course'));
const EditClassroomData = React.lazy(() => import('../modals/edit_classroom'));
const EditStudentData = React.lazy(() => import('../modals/edit_student'));

const DEFAULT_MAGNETBOARD_OPTIONS = {
  show_top_bar: true,
  allow_schedule_name_editing: true,
  allow_filtering: true,
  show_match_percent: true,
  show_conflicts: true,
  show_menu: true,
  hide_edit: false,
  allow_new_sections: true,
  allow_placeholders: true,
  allow_hotlinks: true,
  allow_section_dragging: true,
  allow_section_structure_editing: true,
  allow_section_roster_editing: true,
  allow_locked_section_roster_editing: false,
  allow_section_duplicate: true,
  allow_section_delete: true,
  allow_inline_quarter_editing: true,
  allow_period_availability_editing: true,
  show_section_conflict_indicators: true,
  restrict_to_allowed_ids: false,
  allow_cross_department_editing: false,
  default_view: null,
  on_placement_change_saved: null,
  on_manual_placement_saved: null,
  progress_subscreen: null,
  current_department_id: null,
  current_department: null,
  allowed_department_ids: [],
  allowed_teacher_ids: [],
  allowed_course_ids: [],
  allowed_classroom_ids: [],
  view_options: [
    { value: "teacher", display: "Teacher View" },
    { value: "course", display: "Course View" },
    { value: "classroom", display: "Classroom View" },
    { value: "student", display: "Student View" },
  ],
};

export default function MagnetboardMain({
  schoolSubscribed,
  schoolInfoLoading,
  schoolInfo,
  scheduleInfo,
  setScheduleInfo,
  scheduleStructureData,
  setScheduleStructureData,
  scheduleInfoLoading,
  scheduleStructureDataLoading,
  sectionsLoading,
  teachersLoading,
  coursesLoading,
  classroomsLoading,
  departmentsLoading,
  studentsLoading,
  userInfo,
  sections,
  subsections,
  setSections,
  setLabels,
  setSubsections,
  students,
  setStudents,
  studentMatchPercentages,
  teachers,
  setTeachers,
  courses,
  setCourses,
  classrooms,
  setClassrooms = () => {},
  departments,
  labels,
  lunchInfo,
  setScreenAllowsUniversalSidebar,
  handleDuplicateScheduleVersion,
  handleDeleteScheduleVersion,
  handleUpdateScheduleVersionName,
  magnetboardOptions = DEFAULT_MAGNETBOARD_OPTIONS,
}) {
  const renderCountRef = useRef(0);
  const renderStartedAt = getDiagnosticTimestamp();
  renderCountRef.current += 1;

  const [viewPreferencesLoaded, setViewPreferencesLoaded] = useState(false);
  const [duplicateLoadingTargets, setDuplicateLoadingTargets] = useState({});
  const [deleteLoadingTargets, setDeleteLoadingTargets] = useState({});
  const [pendingViewSelection, setPendingViewSelection] = useState(null);
  const [studentViewTransitionActive, setStudentViewTransitionActive] = useState(false);
  const [deferStudentViewWork, setDeferStudentViewWork] = useState(false);
  const [activePrintMode, setActivePrintMode] = useState(null);
  const [preparingPrintMode, setPreparingPrintMode] = useState(null);
  const [printOptionsModalVisible, setPrintOptionsModalVisible] = useState(false);
  const [excelExportOptionsModalVisible, setExcelExportOptionsModalVisible] = useState(false);
  const [activePrintOptions, setActivePrintOptions] = useState(DEFAULT_PRINT_OPTIONS);
  const [editDataModalInfo, setEditDataModalInfo] = useState(null);
  const pendingViewFrameRef = useRef(null);
  const deferredStudentWorkTimeoutRef = useRef(null);
  const clearPrintModeTimeoutRef = useRef(null);
  const defaultViewAppliedRef = useRef(false);
  const displayScheduleDataSignatureRef = useRef(null);
  const filterOptionsContextRef = useRef(null);

  const { scheduleType, currentSchedule, currentScheduleVersionID, displaySchedule, displayCreated } = useScheduleData();
  const { currentViewType, currentView, periodType, currentDay, periodsToShow } = useViewData();
  const { conflicts, conflictDataKey, conflictCalculationKey, conflictDataRevision } = useConflictData();
  const { isLoading } = useLoadingData();
  const {
    filterIDs,
    filtered,
    filteredData,
    labs_only_filtered,
    non_labs_only_filtered,
    no_classroom_filtered,
    has_classroom_filtered,
    singletons_filtered,
    doubletons_filtered,
    searchValue,
    filterOptions,
  } = useFilterData();
  const { showEditStudentScheduleModal, showExportScheduleModal } = useModalsData();
  const { setEditSectionModalInfo, toggleShowEditSectionModal, setEditStudentScheduleModalInfo, setEditStudentScheduleModal, setShowExportScheduleModal } = useModalsActions();
  const { addOpenReport, setClassroomPlacementReport, setScheduleSummaryScope } = useReportsActions();
  const { printRef, printByTeacherRef } = usePrintData();

  const allowedDepartmentIdSet = useMemo(
    () => new Set((Array.isArray(magnetboardOptions.allowed_department_ids) ? magnetboardOptions.allowed_department_ids : []).map(department_id => `${department_id}`)),
    [magnetboardOptions.allowed_department_ids]
  );

  const sectionIsAllowedForCurrentUser = useCallback((section) => {
    if (!magnetboardOptions.restrict_to_allowed_ids) return true;
    if (magnetboardOptions.allow_cross_department_editing === true || `${magnetboardOptions.allow_cross_department_editing}` === '1') return true;
    if (!section || allowedDepartmentIdSet.size === 0) return false;

    const section_departments = Array.isArray(section.departments) ? section.departments : [];
    if(section_departments.some(department_id => allowedDepartmentIdSet.has(`${department_id}`))) return true;

    const section_course = courses.find(course => `${course.course_id}` === `${section.course_id}`);
    const course_departments = Array.isArray(section_course?.departments) ? section_course.departments : [];
    return course_departments.some(department_id => allowedDepartmentIdSet.has(`${department_id}`));
  }, [allowedDepartmentIdSet, courses, magnetboardOptions.allow_cross_department_editing, magnetboardOptions.restrict_to_allowed_ids]);

  const allowedTeacherIdSet = useMemo(
    () => new Set((Array.isArray(magnetboardOptions.allowed_teacher_ids) ? magnetboardOptions.allowed_teacher_ids : []).map(teacher_id => `${teacher_id}`)),
    [magnetboardOptions.allowed_teacher_ids]
  );
  const allowedCourseIdSet = useMemo(
    () => new Set((Array.isArray(magnetboardOptions.allowed_course_ids) ? magnetboardOptions.allowed_course_ids : []).map(course_id => `${course_id}`)),
    [magnetboardOptions.allowed_course_ids]
  );
  const allowedClassroomIdSet = useMemo(
    () => new Set((Array.isArray(magnetboardOptions.allowed_classroom_ids) ? magnetboardOptions.allowed_classroom_ids : []).map(classroom_id => `${classroom_id}`)),
    [magnetboardOptions.allowed_classroom_ids]
  );

  const visibleDepartments = useMemo(() => {
    if (!magnetboardOptions.restrict_to_allowed_ids) return departments;
    return departments.filter(department => allowedDepartmentIdSet.has(`${department.department_id}`));
  }, [allowedDepartmentIdSet, departments, magnetboardOptions.restrict_to_allowed_ids]);

  const visibleTeachers = useMemo(() => {
    if (!magnetboardOptions.restrict_to_allowed_ids) return teachers;
    return teachers.filter(teacher => allowedTeacherIdSet.has(`${teacher.teacher_id}`));
  }, [allowedTeacherIdSet, magnetboardOptions.restrict_to_allowed_ids, teachers]);

  const visibleCourses = useMemo(() => {
    if (!magnetboardOptions.restrict_to_allowed_ids) return courses;
    return courses.filter(course => allowedCourseIdSet.has(`${course.course_id}`));
  }, [allowedCourseIdSet, courses, magnetboardOptions.restrict_to_allowed_ids]);

  const visibleClassrooms = useMemo(() => {
    if (!magnetboardOptions.restrict_to_allowed_ids) return classrooms;
    return classrooms.filter(classroom => allowedClassroomIdSet.has(`${classroom.classroom_id}`));
  }, [allowedClassroomIdSet, classrooms, magnetboardOptions.restrict_to_allowed_ids]);

  const magnetboardReportScope = useMemo(() => {
    if(!magnetboardOptions.restrict_to_allowed_ids) return null;

    return {
      restrict_to_allowed_ids: true,
      allowed_department_ids: Array.isArray(magnetboardOptions.allowed_department_ids) ? magnetboardOptions.allowed_department_ids : [],
      allowed_teacher_ids: Array.isArray(magnetboardOptions.allowed_teacher_ids) ? magnetboardOptions.allowed_teacher_ids : [],
      allowed_course_ids: Array.isArray(magnetboardOptions.allowed_course_ids) ? magnetboardOptions.allowed_course_ids : [],
      allowed_classroom_ids: Array.isArray(magnetboardOptions.allowed_classroom_ids) ? magnetboardOptions.allowed_classroom_ids : [],
    };
  }, [
    magnetboardOptions.allowed_classroom_ids,
    magnetboardOptions.allowed_course_ids,
    magnetboardOptions.allowed_department_ids,
    magnetboardOptions.allowed_teacher_ids,
    magnetboardOptions.restrict_to_allowed_ids,
  ]);

  const scopedConflictsForReports = useMemo(() => filterConflictsForReportScope({
    conflicts,
    reportScope: magnetboardReportScope,
    sections,
    courses,
  }), [conflicts, courses, magnetboardReportScope, sections]);

  useEffect(() => {
    setScheduleSummaryScope(magnetboardReportScope);
  }, [magnetboardReportScope, setScheduleSummaryScope]);

  const mergeFilterOptionsWithExistingSelections = useCallback((nextFilterOptions = {}) => {
    const currentFilterOptions = filterOptions || {};

    return Object.keys(nextFilterOptions).reduce((results, filterTitle) => {
      const nextOptions = Array.isArray(nextFilterOptions[filterTitle]) ? nextFilterOptions[filterTitle] : [];
      const currentOptions = Array.isArray(currentFilterOptions[filterTitle]) ? currentFilterOptions[filterTitle] : [];
      const currentOptionsByValue = currentOptions.reduce((optionResults, option) => {
        optionResults[`${option.value}`] = option;
        return optionResults;
      }, {});

      results[filterTitle] = nextOptions.map((option) => {
        const currentOption = currentOptionsByValue[`${option.value}`];
        if(!currentOption) return {...option, selected:Boolean(option.selected)};

        const selected = !option.is_header && Boolean(currentOption.selected);
        const mergedOption = {...option, selected};

        if(filterTitle === 'student_range' && selected)
        {
          mergedOption.start_student_id = currentOption.start_student_id || null;
          mergedOption.end_student_id = currentOption.end_student_id || null;
          mergedOption.display = currentOption.display || option.display;
        }

        return mergedOption;
      });

      return results;
    }, {});
  }, [filterOptions]);

  const displayScheduleDataSignature = useMemo(() => ([
    schoolInfo?.school_id || '',
    schoolInfo?.current_schedule_version_id || '',
    currentScheduleVersionID || '',
    currentView || '',
    currentViewType || '',
    visibleTeachers.length,
    visibleCourses.length,
    visibleClassrooms.length,
    visibleDepartments.length,
    students.length,
    sections.length,
    subsections.length,
    Array.isArray(labels) ? labels.length : 0,
  ].join(':')), [
    schoolInfo?.school_id,
    schoolInfo?.current_schedule_version_id,
    currentScheduleVersionID,
    currentView,
    currentViewType,
    visibleTeachers.length,
    visibleCourses.length,
    visibleClassrooms.length,
    visibleDepartments.length,
    students.length,
    sections.length,
    subsections.length,
    labels,
  ]);
  const filterOptionsContext = [
    schoolInfo?.school_id || '',
    schoolInfo?.current_schedule_version_id || '',
    currentScheduleVersionID || '',
    currentView || '',
    currentViewType || '',
  ].join(':');

  const notifyPlacementChangeSaved = useCallback((details = {}) => {
    if (typeof magnetboardOptions.on_placement_change_saved === "function") {
      magnetboardOptions.on_placement_change_saved(details);
    }

    if (details.change_type === "manual_placement" && typeof magnetboardOptions.on_manual_placement_saved === "function") {
      magnetboardOptions.on_manual_placement_saved(details);
    }
  }, [magnetboardOptions.on_manual_placement_saved, magnetboardOptions.on_placement_change_saved]);

  const finishSwalConfirmLoading = useCallback(() => {
    if(typeof swal.stopLoading === "function") swal.stopLoading();
    swal.close();
  }, []);

  const waitForBrowserPaint = useCallback(() => new Promise(resolve => {
    const runAfterFrame = typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame
      : (callback) => setTimeout(callback, 0);
    runAfterFrame(() => runAfterFrame(resolve));
  }), []);

  const { initializeSchedule, updatePeriodType, loadFromSessionStorage, setCurrentScheduleVersionID, setCurrentSchedule, setOtherSchedulesVisible, updateStore } =
    useScheduleActions();
  const { setPeriodsToShow, changeCurrentView } = useViewActions();
  const {
    setMatchPercent,
    setMatchPercentLoading,
    setConflicts,
    setConflictsLoading,
    setConflictCalculationKey,
    markConflictDataFresh,
  } = useConflictActions();
  const { setIsLoading, setIsLockSectionLoading, setIsLockSectionIds } = useLoadingActions();
  const printDetailDisplayStyle = activePrintOptions.showClassroom || activePrintOptions.showStudentCount
    ? `
      .mb-print-shell .mb-section-subname-container {
        display: grid !important;
      }
    `
    : "";
  const printPageWidth = activePrintOptions.orientation === "portrait" ? "8.5in" : "11in";
  const printPageHeight = activePrintOptions.orientation === "portrait" ? "11in" : "8.5in";
  const printPageSize = `${printPageWidth} ${printPageHeight}`;
  const printSchedulePageStyle = useMemo(() => MAGNETBOARD_PRINT_PAGE_STYLE + `
    @page {
      size: ${printPageSize};
      margin: 8mm;
    }
    @page magnetboard-schedule-page {
      size: ${printPageSize};
      margin: 8mm;
    }
    @media print {
      @page {
        size: ${printPageSize};
        margin: 8mm;
      }
      html,
      body {
        width: ${printPageWidth} !important;
        min-width: ${printPageWidth} !important;
      }
      .mb-print-screen-hidden {
        page: magnetboard-schedule-page;
        width: ${printPageWidth} !important;
        max-width: ${printPageWidth} !important;
      }
    }
    .mb-print-shell .mb-section-quarter-day-container {
      display: flex !important;
    }
    .mb-print-shell .mb-section-quarter-day {
      font-size: 6px !important;
    }
    ${printDetailDisplayStyle}
  `, [printDetailDisplayStyle, printPageSize, printPageWidth]);
  const printMagnetboardSchedule = useCallback((printIframe) => new Promise((resolve, reject) => {
    const printWindow = printIframe?.contentWindow;
    const printDocument = printIframe?.contentDocument || printWindow?.document;

    if (!printWindow || !printDocument?.head) {
      reject(new Error("Magnetboard print iframe was not ready."));
      return;
    }

    try {
      printDocument.querySelectorAll('style[data-magnetboard-print-orientation="true"]').forEach(styleElement => {
        styleElement.remove();
      });

      const finalOrientationStyle = printDocument.createElement("style");
      finalOrientationStyle.setAttribute("data-magnetboard-print-orientation", "true");
      finalOrientationStyle.textContent = `
        @page {
          size: ${printPageSize};
          margin: 8mm;
        }
        @page magnetboard-schedule-page {
          size: ${printPageSize};
          margin: 8mm;
        }
        @media print {
          @page {
            size: ${printPageSize};
            margin: 8mm;
          }
          html,
          body {
            width: ${printPageWidth} !important;
            min-width: ${printPageWidth} !important;
          }
          .mb-print-screen-hidden {
            page: magnetboard-schedule-page;
            width: ${printPageWidth} !important;
            max-width: ${printPageWidth} !important;
          }
        }
      `;
      printDocument.head.appendChild(finalOrientationStyle);

      printWindow.focus();
      printWindow.print();
      resolve();
    } catch (error) {
      reject(error);
    }
  }), [printPageSize, printPageWidth]);
  const openPrint = useReactToPrint({
    content: () => printRef?.current,
    documentTitle: "magnetboard-schedule",
    pageStyle: printSchedulePageStyle,
    print: printMagnetboardSchedule,
  });
  const openPrintByTeacher = useReactToPrint({
    content: () => printByTeacherRef?.current,
    documentTitle: "magnetboard-schedule-by-teacher",
    pageStyle: MAGNETBOARD_PRINT_PAGE_STYLE + `
      @page {
        size: 11in 8.5in;
        margin: 8mm;
      }
      .mb-print-teacher-page {
        break-after: page;
        page-break-after: always;
      }
      .mb-print-shell .mb-section-quarter-day-container {
        display: flex !important;
      }
      .mb-print-shell .mb-section-top-bar.red,
      .mb-print-shell .mb-section-top-bar.orange {
        background-color: #3b82f6 !important;
      }
      .mb-print-shell .mb-section-locked-visual {
        background-color: #fff !important;
        border-color: #d9d9d9 !important;
      }
      .mb-print-shell .mb-section-quarter-day {
        font-size: 6px !important;
      }
    `,
  });

  useEffect(() => {
    logMagnetboardDiagnostic('main-commit', {
      render_count: renderCountRef.current,
      duration_ms: Math.round(getDiagnosticTimestamp() - renderStartedAt),
      current_view: currentView,
      display_created: displayCreated,
      is_loading: isLoading,
      sections: sections.length,
      subsections: subsections.length,
      students: students.length,
      teachers: teachers.length,
      courses: courses.length,
      classrooms: classrooms.length,
      conflicts: conflicts.length,
    });
  });

  // Initialize
  useEffect(() => {
    if (
      !scheduleInfoLoading &&
      !scheduleStructureDataLoading &&
      !schoolInfoLoading &&
      !isEmpty(scheduleInfo) &&
      !isEmpty(schoolInfo)
    ) {
      initializeSchedule(scheduleInfo, schoolInfo, scheduleStructureData);
      setScreenAllowsUniversalSidebar(true);
    }
  }, [
    scheduleInfoLoading,
    scheduleStructureDataLoading,
    schoolInfoLoading,
    scheduleInfo,
    scheduleStructureData,
    schoolInfo,
    initializeSchedule,
    setScreenAllowsUniversalSidebar
  ]);

  useEffect(() => {
    if (!viewPreferencesLoaded) return;
    if (magnetboardOptions.view_options && magnetboardOptions.view_options.length > 0) {
      if (!magnetboardOptions.view_options.find(option => option.value === currentView) && magnetboardOptions.view_options[0].value !== currentView) {
        changeCurrentView(magnetboardOptions.view_options[0].value);
      }
    }
  }, [viewPreferencesLoaded, currentView, changeCurrentView, magnetboardOptions.view_options]);

  const clearPendingViewTransition = useCallback(() => {
    if (pendingViewFrameRef.current) {
      window.cancelAnimationFrame(pendingViewFrameRef.current);
      pendingViewFrameRef.current = null;
    }

    if (deferredStudentWorkTimeoutRef.current) {
      window.clearTimeout(deferredStudentWorkTimeoutRef.current);
      deferredStudentWorkTimeoutRef.current = null;
    }
  }, []);

  const handleChangeViewRequest = useCallback((new_view) => {
    if (!new_view || new_view === currentView) {
      setPendingViewSelection(null);
      return;
    }

    clearPendingViewTransition();

    if (new_view === "student") {
      setPendingViewSelection(new_view);
      setStudentViewTransitionActive(true);
      setDeferStudentViewWork(true);

      pendingViewFrameRef.current = window.requestAnimationFrame(() => {
        pendingViewFrameRef.current = window.requestAnimationFrame(() => {
          pendingViewFrameRef.current = null;
          changeCurrentView(new_view);
        });
      });
      return;
    }

    if (currentView === "student") {
      setPendingViewSelection(new_view);

      pendingViewFrameRef.current = window.requestAnimationFrame(() => {
        pendingViewFrameRef.current = window.requestAnimationFrame(() => {
          pendingViewFrameRef.current = null;
          changeCurrentView(new_view);
        });
      });
      return;
    }

    setPendingViewSelection(null);
    setStudentViewTransitionActive(false);
    setDeferStudentViewWork(false);
    changeCurrentView(new_view);
  }, [changeCurrentView, clearPendingViewTransition, currentView]);

  useEffect(() => {
    if(!viewPreferencesLoaded || !magnetboardOptions.default_view || defaultViewAppliedRef.current) return;

    defaultViewAppliedRef.current = true;
    if(currentView !== magnetboardOptions.default_view) handleChangeViewRequest(magnetboardOptions.default_view);
  }, [currentView, handleChangeViewRequest, magnetboardOptions.default_view, viewPreferencesLoaded]);

  useEffect(() => {
    return () => {
      clearPendingViewTransition();
      if (clearPrintModeTimeoutRef.current) {
        window.clearTimeout(clearPrintModeTimeoutRef.current);
        clearPrintModeTimeoutRef.current = null;
      }
    };
  }, [clearPendingViewTransition]);

  const requestPrintSchedule = useCallback((printMode, printRequestOptions = null) => {
    if (printMode === "schedule" && !printRequestOptions) {
      setPrintOptionsModalVisible(true);
      return;
    }

    if (clearPrintModeTimeoutRef.current) {
      window.clearTimeout(clearPrintModeTimeoutRef.current);
      clearPrintModeTimeoutRef.current = null;
    }

    setPrintOptionsModalVisible(false);
    setActivePrintOptions(printRequestOptions?.printOptions || DEFAULT_PRINT_OPTIONS);
    setPreparingPrintMode(printMode);
    setActivePrintMode(printMode);
    logMagnetboardDiagnostic('print-requested', {print_mode: printMode});
  }, []);

  useEffect(() => {
    if (!activePrintMode || !preparingPrintMode) return undefined;

    const activePrintRef = activePrintMode === 'by_teacher' ? printByTeacherRef : printRef;
    if (!activePrintRef?.current) return undefined;

    let animationFrameId = null;
    animationFrameId = window.requestAnimationFrame(() => {
      logMagnetboardDiagnostic('print-open', {print_mode: activePrintMode});

      if (activePrintMode === 'by_teacher') {
        if (typeof openPrintByTeacher === 'function') openPrintByTeacher();
      } else if (typeof openPrint === 'function') {
        openPrint();
      }

      setPreparingPrintMode(null);
      clearPrintModeTimeoutRef.current = window.setTimeout(() => {
        setActivePrintMode(null);
        clearPrintModeTimeoutRef.current = null;
      }, 1000);
    });

    return () => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [activePrintMode, openPrint, openPrintByTeacher, preparingPrintMode, printByTeacherRef, printRef]);

  useEffect(() => {
    if (currentView !== "student") {
      if (pendingViewSelection === "student") return;

      setStudentViewTransitionActive(false);
      setPendingViewSelection(null);
      setDeferStudentViewWork(false);
      return;
    }

    if (!studentViewTransitionActive || isLoading || !displayCreated) return;

    let animationFrameId = null;

    animationFrameId = window.requestAnimationFrame(() => {
      setStudentViewTransitionActive(false);
      deferredStudentWorkTimeoutRef.current = window.setTimeout(() => {
        setDeferStudentViewWork(false);
        setPendingViewSelection(null);
        deferredStudentWorkTimeoutRef.current = null;
      }, 0);
    });

    return () => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [currentView, displayCreated, isLoading, pendingViewSelection, studentViewTransitionActive]);

  // useEffects
  useEffect(() => {
    if (
      !schoolInfoLoading &&
      !scheduleInfoLoading &&
      !sectionsLoading &&
      !teachersLoading &&
      !coursesLoading &&
      !classroomsLoading &&
      !studentsLoading
    ) {
      const next_conflict_data_key = `${schoolInfo.school_id}:${schoolInfo.current_schedule_version_id}:${conflictDataRevision}`;
      if(conflictDataKey === next_conflict_data_key || conflictCalculationKey === next_conflict_data_key) return;

      const loadConflictData = async () => {
        setConflictsLoading(true);
        setMatchPercentLoading(true);
        setConflictCalculationKey(next_conflict_data_key);

        try {
          const conflicts_response = await calculateConflicts({
            school_id: schoolInfo.school_id,
            schedule_version_id: schoolInfo.current_schedule_version_id,
            match_percent: true,
          });
          const conflicts_list = Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : [];
          setConflicts([...conflicts_list]);
          setMatchPercent(conflicts_response.match_percent);
          markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
        } catch (error) {
          console.log(error?.response || error);
          setConflicts([]);
        } finally {
          setConflictsLoading(false);
          setMatchPercentLoading(false);
          setConflictCalculationKey(null);
        }
      };

      loadConflictData();
    }
  }, [
    schoolInfoLoading,
    scheduleInfoLoading,
    sectionsLoading,
    teachersLoading,
    coursesLoading,
    classroomsLoading,
    studentsLoading,
    schoolInfo.school_id,
    schoolInfo.current_schedule_version_id,
    conflictDataKey,
    conflictCalculationKey,
    conflictDataRevision,
    setConflicts,
    setMatchPercent,
    setConflictsLoading,
    setMatchPercentLoading,
    setConflictCalculationKey,
    markConflictDataFresh,
  ]);

  useEffect(() => {
    if (scheduleType && currentViewType) {
      updatePeriodType();
    }
  }, [scheduleType, currentViewType, updatePeriodType]);

  useEffect(() => {
    if (schoolInfoLoading || scheduleInfoLoading || viewPreferencesLoaded) return;
    loadFromSessionStorage();
    setViewPreferencesLoaded(true);
  }, [schoolInfoLoading, scheduleInfoLoading, viewPreferencesLoaded, loadFromSessionStorage]);

  // Create periods to show
  useEffect(() => {
    if(isEmpty(scheduleStructureData) || !periodType) return;

    const sorted_periods = buildPeriodsToShow(scheduleStructureData, {
      period_type: periodType,
      current_day: currentDay,
      natural_sort_course_periods: true,
    });

    setPeriodsToShow(sorted_periods);
  }, [periodType, scheduleStructureData, currentDay, setPeriodsToShow]);

  // Create magnetboard display (will also create filter options)
  useEffect(() => {
    const createDisplaySchedule = () => {
      const endTimer = startMagnetboardDiagnosticTimer('create-display-schedule', {
        current_view: currentView,
        sections: sections.length,
        students: students.length,
        teachers: visibleTeachers.length,
        courses: visibleCourses.length,
        classrooms: visibleClassrooms.length,
        departments: visibleDepartments.length,
      });

      const should_preserve_filter_selections = filterOptionsContextRef.current === filterOptionsContext;
      if(!should_preserve_filter_selections) setIsLoading(true);
      
      // Organize schedules for display
      let display_schedule = [];
      let filter_options = {};
      
      if(currentView === 'teacher')
      {
        const sorted_departments = sortObject(visibleDepartments, 'department', 'text');
        const sorted_teachers = sortArrayOfObjects(visibleTeachers, 'name', 'text', 'asc');
        const sorted_classrooms = sortArrayOfObjects(visibleClassrooms, 'classroom_name', 'text', 'asc');
        const show_department_filter = !magnetboardOptions.restrict_to_allowed_ids || sorted_departments.length > 1;

        filter_options = {...(show_department_filter ? {'departments': []} : {}), 'teachers':[], 'classrooms':[], 'classroom_status':[{display:"No Classroom", value:"no_classroom"}, {display:"Has Classroom", value:"has_classroom"}], 'singletons':[{display:"Singletons", value:"singletons"}, {display:"Doubletons", value:"doubletons"}], 'labs': [{display:"Labs Only", value:"labs_only"}, {display:"Non-Labs Only", value:"non_labs_only"}]};
        if(show_department_filter && sorted_teachers.some(teacher => hasNoDepartmentValues(teacher.departments))) filter_options['departments'].push({display:NO_DEPARTMENT_FILTER_DISPLAY, value:NO_DEPARTMENT_FILTER_VALUE});
        
        sorted_departments.forEach(department => {
          if(!show_department_filter) return;
          const department_id = department.department_id;
          filter_options['departments'].push({display:department.department, value:department_id});
        });

        for(const teacher of sorted_teachers)
        {
          const teacher_id = teacher.teacher_id;
          const teacher_last_name = teacher.name;
          const teacher_first_name= teacher.first_name;
          const teacher_display_name = `${teacher_last_name}, ${teacher_first_name}`;

          filter_options['teachers'].push({display:teacher_display_name, value:teacher_id});

          display_schedule.push({data_id:teacher_id, display_name:teacher_display_name, display_info:teacher});
        }

        for(const classroom of sorted_classrooms)
        {
          const classroom_id = classroom.classroom_id;
          const classroom_name = classroom.classroom_name;

          filter_options['classrooms'].push({display:classroom_name, value:classroom_id});
        }
      }
      else if(currentView === 'course')
      {
        const sorted_departments = sortObject(visibleDepartments, 'department', 'text');
        const sorted_courses = sortArrayOfObjects(visibleCourses, 'name', 'text', 'asc');
        const sorted_teachers = sortArrayOfObjects(visibleTeachers, 'name', 'text', 'asc');
        const sorted_classrooms = sortArrayOfObjects(visibleClassrooms, 'classroom_name', 'text', 'asc');
        const show_department_filter = !magnetboardOptions.restrict_to_allowed_ids || sorted_departments.length > 1;

        filter_options = {...(show_department_filter ? {'departments': []} : {}), 'courses': [], 'teachers':[], 'classrooms':[], 'periods': [{display: 'Unplaced', value: UNPLACED_FILTER_VALUE}], 'terms': [{display: 'Unplaced', value: UNPLACED_FILTER_VALUE}], 'classroom_status':[{display:"No Classroom", value:"no_classroom"}, {display:"Has Classroom", value:"has_classroom"}], 'singletons':[{display:"Singletons", value:"singletons"}, {display:"Doubletons", value:"doubletons"}], 'labs': [{display:"Labs Only", value:"labs_only"}, {display:"Non-Labs Only", value:"non_labs_only"}]};
        if(show_department_filter && sorted_courses.some(course => hasNoDepartmentValues(course.departments))) filter_options['departments'].push({display:NO_DEPARTMENT_FILTER_DISPLAY, value:NO_DEPARTMENT_FILTER_VALUE});

        sorted_departments.forEach(department => {
          if(!show_department_filter) return;
          const department_id = department.department_id;
          filter_options['departments'].push({display:department.department, value:department_id});
        });

        for(const course of sorted_courses)
        {
          const course_id = course.course_id;
          const course_name = course.name;
          const course_code = course.course_code;
          const course_display_name = `${course_name} (${course_code})`;

          filter_options['courses'].push({display:course_display_name, value:course_id});
          display_schedule.push({data_id:course_id, display_name:course_display_name, display_info:course});
        }

        for(const teacher of sorted_teachers)
        {
          const teacher_id = teacher.teacher_id;
          const teacher_last_name = teacher.name;
          const teacher_first_name= teacher.first_name;
          const teacher_display_name = `${teacher_last_name}, ${teacher_first_name}`;

          filter_options['teachers'].push({display:teacher_display_name, value:teacher_id});
        }

        for(const classroom of sorted_classrooms)
        {
          const classroom_id = classroom.classroom_id;
          const classroom_name = classroom.classroom_name;

          filter_options['classrooms'].push({display:classroom_name, value:classroom_id});
        }

        Object.keys(scheduleStructureData?.course_periods || {}).reduce((results, course_period_id) => {
          const period_name = getPeriodNameFromCoursePeriodId(scheduleStructureData, course_period_id, {
            schedule_type: scheduleInfo.schedule_type,
            current_day: '1',
          });
          if(!period_name) return results;
          results.push({display: period_name, value: course_period_id});
          return results;
        }, []).sort((period_a, period_b) => `${period_a.display}`.localeCompare(`${period_b.display}`, undefined, {numeric:true, sensitivity:'base'})).forEach((period_option) => {
          filter_options['periods'].push(period_option);
        });

        getTermFilterOptionsFromSections(sections, {includeUnplaced: false}).forEach((term_option) => {
          filter_options['terms'].push({display: term_option.display, value: term_option.value});
        });
      }
      else if(currentView === 'classroom')
      {
        const sorted_departments = sortObject(visibleDepartments, 'department', 'text');
        const sorted_classrooms = sortArrayOfObjects(visibleClassrooms, 'classroom_name', 'text', 'asc');
        const show_department_filter = !magnetboardOptions.restrict_to_allowed_ids || sorted_departments.length > 1;

        filter_options = {...(show_department_filter ? {'departments': []} : {}), 'classrooms':[], 'singletons':[{display:"Singletons", value:"singletons"}, {display:"Doubletons", value:"doubletons"}], 'labs': [{display:"Labs Only", value:"labs_only"}, {display:"Non-Labs Only", value:"non_labs_only"}]};
        if(show_department_filter && sorted_classrooms.some(classroom => hasNoDepartmentValues(classroom.departments))) filter_options['departments'].push({display:NO_DEPARTMENT_FILTER_DISPLAY, value:NO_DEPARTMENT_FILTER_VALUE, selected:false});
        
        sorted_departments.forEach(department => {
          if(!show_department_filter) return;
          const department_id = department.department_id;
          filter_options['departments'].push({display:department.department, value:department_id, selected:false});
        });

        for(const classroom of sorted_classrooms)
        {
          const classroom_id = classroom.classroom_id;
          const classroom_name = classroom.classroom_name;

          filter_options['classrooms'].push({display:classroom_name, value:classroom_id});
          display_schedule.push({data_id:classroom_id, display_name:classroom_name, display_info:classroom});
        }
      }
      else if(currentView === 'student')
      {
        const sorted_courses = sortArrayOfObjects(visibleCourses, 'name', 'text', 'asc');

        filter_options = {'student_range':[{value:'student_range', display:'Student Range', selected:false, start_student_id:null, end_student_id:null}], 'grades':[], 'status': [{value:'1', display:'Complete (100%)', selected:false}, {value:'.75', display:'Mostly Complete (>75%)', selected:false}, {value:'.5', display:'Needs Work (<75%)', selected:false}], 'special ed':[{value:'1', display:'Special Ed', selected:false}, {value:'0', display:'Not Special Ed', selected:false}]}

        const grades = students.reduce((result, student) => {
          const student_grade = student.grade;
          if(result.findIndex(option => option.value === student_grade) === -1) result.push({display:student_grade, value:student_grade, selected:false});
          return result;
        }, []);
        const sorted_grades = sortArrayOfObjects(grades, 'value', 'number', 'asc');
        const labels_by_id = (labels || []).reduce((result, label) => {
          result[`${label.label_id}`] = label;
          return result;
        }, {});
        const team_options_by_id = students.reduce((result, student) => {
          const student_labels = Array.isArray(student.student_labels) ? student.student_labels : [];
          student_labels.forEach((student_label) => {
            const label_id = `${student_label?.label_id || ''}`;
            const label_info = labels_by_id[label_id];
            if(!label_id || !label_info) return;

            if(!result[label_id])
            {
              result[label_id] = {
                display: label_info.label,
                value: label_id,
                selected: false,
                grades: new Set(),
              };
            }

            if(student.grade) result[label_id].grades.add(`${student.grade}`);
          });
          return result;
        }, {});
        const sorted_team_options = Object.values(team_options_by_id).map((team_option) => {
          const sorted_team_grades = [...team_option.grades].sort((grade_a, grade_b) => `${grade_a}`.localeCompare(`${grade_b}`, undefined, {numeric:true, sensitivity:'base'}));
          const team_grade = sorted_team_grades[0] || '';
          return {
            display: team_option.display,
            value: team_option.value,
            selected: false,
            grade_display: team_grade ? `Grade ${team_grade}` : 'No Grade',
            grade_sort_value: team_grade,
            team_sort_value: team_option.display,
          };
        }).sort((team_a, team_b) => {
          const grade_comparison = `${team_a.grade_sort_value}`.localeCompare(`${team_b.grade_sort_value}`, undefined, {numeric:true, sensitivity:'base'});
          if(grade_comparison !== 0) return grade_comparison;
          return `${team_a.team_sort_value}`.localeCompare(`${team_b.team_sort_value}`, undefined, {numeric:true, sensitivity:'base'});
        }).reduce((results, team_option) => {
          const previous_option = results[results.length - 1];
          if(!previous_option || previous_option.grade_display !== team_option.grade_display)
          {
            results.push({
              display: team_option.grade_display,
              value: `team-grade-header-${team_option.grade_sort_value || 'none'}`,
              selected: false,
              is_header: true,
              grade_display: team_option.grade_display,
            });
          }

          results.push(team_option);
          return results;
        }, []);

        filter_options['grades'].push(...sorted_grades);
        if(sorted_team_options.length > 0)
        {
          filter_options['teams'] = sorted_team_options;
        }
        filter_options['courses'] = [];

        for(const student of students)
        {
          const student_id = student.student_id;
          display_schedule.push({data_id:student_id, display_name:null, display_info:student});
        }

        for(const course of sorted_courses)
        {
          const course_id = course.course_id;
          const course_name = course.name;
          const course_code = course.course_code;
          const course_display_name = `${course_name} (${course_code})`;

          filter_options['courses'].push({display:course_display_name, value:course_id});
        }
      }

      updateStore({
        displaySchedule: [...display_schedule],
        isLoading: false,
        displayCreated: true,
        filterOptions: should_preserve_filter_selections ? mergeFilterOptionsWithExistingSelections(filter_options) : {...filter_options},
      });
      filterOptionsContextRef.current = filterOptionsContext;
      displayScheduleDataSignatureRef.current = displayScheduleDataSignature;
      endTimer({
        display_rows: display_schedule.length,
        filter_groups: Object.keys(filter_options).length,
      });
    }

    let animationFrameId = null;
    const data_signature_changed = displayScheduleDataSignatureRef.current !== displayScheduleDataSignature;
    const should_create_display_schedule = isLoading || data_signature_changed;

    if(currentView === 'student')
    {
      if(!scheduleInfoLoading && !scheduleStructureDataLoading && !sectionsLoading && !teachersLoading && !coursesLoading && !classroomsLoading && !studentsLoading && should_create_display_schedule) {
        animationFrameId = window.requestAnimationFrame(() => {
          createDisplaySchedule();
        });
      }
    }
    else
    {
      if(!scheduleInfoLoading && !scheduleStructureDataLoading && !sectionsLoading && !teachersLoading && !coursesLoading && !classroomsLoading && !departmentsLoading && should_create_display_schedule) createDisplaySchedule();
    }

    return () => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [scheduleInfoLoading, scheduleStructureData, sectionsLoading, teachersLoading, classroomsLoading, studentsLoading, departmentsLoading, currentView, currentViewType, isLoading, students, labels, setIsLoading, updateStore, visibleDepartments, visibleTeachers, visibleClassrooms, visibleCourses, coursesLoading, scheduleStructureDataLoading, magnetboardOptions.restrict_to_allowed_ids, displayScheduleDataSignature, mergeFilterOptionsWithExistingSelections, filterOptionsContext]);

  useEffect(() => {
    if(!displayCreated || !currentView || displaySchedule.length === 0) return;

    const current_data = currentView === 'teacher'
      ? teachers
      : currentView === 'course'
        ? courses
        : currentView === 'classroom'
          ? classrooms
          : students;
    const id_key = currentView === 'teacher'
      ? 'teacher_id'
      : currentView === 'course'
        ? 'course_id'
        : currentView === 'classroom'
          ? 'classroom_id'
          : 'student_id';
    const current_data_by_id = new Map(current_data.map(data_info => [`${data_info[id_key]}`, data_info]));
    let display_changed = false;

    const next_display_schedule = displaySchedule.map(display_row => {
      const next_display_info = current_data_by_id.get(`${display_row.data_id}`);
      if(!next_display_info || next_display_info === display_row.display_info) return display_row;

      display_changed = true;
      let display_name = display_row.display_name;
      if(currentView === 'teacher') display_name = `${next_display_info.name}, ${next_display_info.first_name}`;
      if(currentView === 'course') display_name = `${next_display_info.name} (${next_display_info.course_code})`;
      if(currentView === 'classroom') display_name = next_display_info.classroom_name;

      return {
        ...display_row,
        display_name,
        display_info: next_display_info,
      };
    });

    if(display_changed) updateStore({displaySchedule:next_display_schedule});
  }, [classrooms, courses, currentView, displayCreated, displaySchedule, students, teachers, updateStore]);

  // Memos
  const scheduleTopBarMemo = useMemo(
    () => ({
      scheduleInfo,
      setScheduleInfo,
      schoolInfo,
    }),
    [scheduleInfo, setScheduleInfo, schoolInfo]
  );
  const scheduleMenuMemo = useMemo(
    () => ({
      scheduleInfo,
      schoolInfo,
      schoolSubscribed,
      courses,
      userInfo,
    }),
    [scheduleInfo, schoolInfo, schoolSubscribed, courses, userInfo]
  );
  const scheduleFiltersMemo = useMemo(
    () => ({
      courses,
      teachers,
      sections,
      classrooms,
      students,
      studentMatchPercentages,
    }),
    [courses, teachers, sections, classrooms, students, studentMatchPercentages]
  );
  const sectionSingletonDoubletonMap = useMemo(() => buildSectionSingletonDoubletonMap({
    sections,
    subsections,
    separateICRCodes: scheduleInfo?.separate_ICR_codes,
  }), [scheduleInfo.separate_ICR_codes, sections, subsections]);
  const sectionsAvailableForRowFilters = useMemo(
    () => sections.filter(section => sectionIsAllowedForCurrentUser(section)),
    [sectionIsAllowedForCurrentUser, sections]
  );
  const filteredDisplaySchedule = useMemo(
    () =>
      getFilteredDisplaySchedule({
        displaySchedule,
        currentView,
        sections: sectionsAvailableForRowFilters,
        sectionSingletonDoubletonMap,
        allowedTeacherIds: magnetboardOptions.allowed_teacher_ids,
        allowedCourseIds: magnetboardOptions.allowed_course_ids,
        allowedClassroomIds: magnetboardOptions.allowed_classroom_ids,
        restrictToAllowedIds: magnetboardOptions.restrict_to_allowed_ids,
        filteredData,
        filterIDs,
        filtered,
        filterOptions,
        labsOnlyFiltered: labs_only_filtered,
        nonLabsOnlyFiltered: non_labs_only_filtered,
        singletonsFiltered: singletons_filtered,
        doubletonsFiltered: doubletons_filtered,
        hasClassroomFiltered: has_classroom_filtered,
        noClassroomFiltered: no_classroom_filtered,
      }),
    [
      currentView,
      displaySchedule,
      filtered,
      filterIDs,
      filteredData,
      filterOptions,
      has_classroom_filtered,
      labs_only_filtered,
      magnetboardOptions.allowed_course_ids,
      magnetboardOptions.allowed_classroom_ids,
      magnetboardOptions.allowed_teacher_ids,
      magnetboardOptions.restrict_to_allowed_ids,
      no_classroom_filtered,
      non_labs_only_filtered,
      sectionSingletonDoubletonMap,
      sectionsAvailableForRowFilters,
      singletons_filtered,
      doubletons_filtered,
    ]
  );
  const filteredStudentNavigationIDs = useMemo(() => {
    if(currentView !== "student") return null;

    return filteredDisplaySchedule
      .map((entity) => entity.data_id)
      .filter((student_id) => student_id !== null && student_id !== undefined);
  }, [currentView, filteredDisplaySchedule]);
  const activePrintFilteredDisplaySchedule = filteredDisplaySchedule;
  const studentSectionsByStudentCoursePeriod = useMemo(() => {
    const sections_by_student_course_period = {};

    sections.forEach((section) => {
      const student_ids = Array.isArray(section.student_list) ? section.student_list : [];
      if (student_ids.length === 0) return;

      const course_period_ids = Array.isArray(section.course_periods) ? section.course_periods : [];

      student_ids.forEach((student_id) => {
        const student_key = `${student_id}`;
        course_period_ids.forEach((course_period_id) => {
          const student_period_key = `${student_key}::${course_period_id}`;
          if (!sections_by_student_course_period[student_period_key]) {
            sections_by_student_course_period[student_period_key] = [];
          }
          sections_by_student_course_period[student_period_key].push(section);
        });
      });
    });

    return sections_by_student_course_period;
  }, [sections]);
  const studentConflictSectionLookup = useMemo(() => {
    const relevant_types = new Set([
      "student_double_booked",
      "student_conflicting_periods",
    ]);
    const lookup = new Set();

    conflicts.forEach((conflict) => {
      if (
        !relevant_types.has(conflict.type) ||
        conflict.ignored === true ||
        conflict.is_deleted === "1"
      ) {
        return;
      }

      const conflict_level =
        conflict.level || (conflict.hard_conflict === "1" ? "conflict" : "warning");
      if (conflict_level !== "conflict") return;

      const student_ids = Array.isArray(conflict.students) ? conflict.students : [];
      const section_ids = Array.isArray(conflict.sections) ? conflict.sections : [];
      if (student_ids.length === 0 || section_ids.length === 0) return;

      student_ids.forEach((student_id) => {
        section_ids.forEach((section_id) => {
          lookup.add(`${student_id}::${section_id}`);
        });
      });
    });

    return lookup;
  }, [conflicts]);
  const sectionConflictSummaryBySectionId = useMemo(() => {
    const lookup = {};

    conflicts.forEach((conflict) => {
      if (
        conflict.ignored === true ||
        conflict.is_deleted === "1" ||
        conflict.type === "student_double_booked" ||
        conflict.type === "student_conflicting_periods"
      ) {
        return;
      }

      const section_ids = Array.isArray(conflict.sections) ? conflict.sections : [];
      if (section_ids.length === 0) return;

      const conflict_level =
        conflict.level || (conflict.hard_conflict === "1" ? "conflict" : "warning");

      section_ids.forEach((section_id) => {
        const section_key = `${section_id}`;
        if (!lookup[section_key]) {
          lookup[section_key] = {
            hasHardConflict: false,
            hasWarning: false,
          };
        }

        if (conflict_level === "conflict") lookup[section_key].hasHardConflict = true;
        if (conflict_level === "warning") lookup[section_key].hasWarning = true;
      });
    });

    return lookup;
  }, [conflicts]);
  const sectionsById = useMemo(
    () =>
      sections.reduce((results, section) => {
        results[`${section.section_id}`] = section;
        return results;
      }, {}),
    [sections]
  );
  const subsectionParentById = useMemo(
    () =>
      subsections.reduce((results, subsection_info) => {
        results[`${subsection_info.subsection_id}`] = `${subsection_info.section_id}`;
        return results;
      }, {}),
    [subsections]
  );
  const teachersById = useMemo(
    () =>
      teachers.reduce((results, teacher) => {
        results[`${teacher.teacher_id}`] = teacher;
        return results;
      }, {}),
    [teachers]
  );
  const coursesById = useMemo(
    () =>
      courses.reduce((results, course) => {
        results[`${course.course_id}`] = course;
        return results;
      }, {}),
    [courses]
  );
  const courseFillPercentagesByCourseId = useMemo(() => {
    const courses_by_id = courses.reduce((results, course_info) => {
      results[`${course_info.course_id}`] = course_info;
      return results;
    }, {});

    const sections_by_id = sections.reduce((results, section_info) => {
      results[`${section_info.section_id}`] = section_info;
      return results;
    }, {});

    const fill_totals_by_course_id = {};
    const logged_main_section_ids = new Set();

    sections.forEach((section_info) => {
      if (!sectionIsAllowedForCurrentUser(section_info)) return;

      const section_course_periods = Array.isArray(section_info.course_periods)
        ? section_info.course_periods
        : [];
      if (section_course_periods.length === 0) return;

      const main_section_id = subsectionParentById[`${section_info.section_id}`]
        ? `${subsectionParentById[`${section_info.section_id}`]}`
        : `${section_info.section_id}`;
      if (logged_main_section_ids.has(main_section_id)) return;
      logged_main_section_ids.add(main_section_id);

      const main_section_info = sections_by_id[main_section_id];
      if (!main_section_info) return;

      const course_id_key = `${main_section_info.course_id || ""}`;
      if (!course_id_key) return;

      const course_info = courses_by_id[course_id_key] || {};
      const max_class_size = parseInt(course_info.max_class_size, 10);
      if (!Number.isInteger(max_class_size) || max_class_size <= 0) return;

      const main_student_count = Array.isArray(main_section_info.student_list)
        ? main_section_info.student_list.length
        : 0;
      const subsection_student_count = (Array.isArray(main_section_info.subsections)
        ? main_section_info.subsections
        : []
      ).reduce((results, subsection_id) => {
        const subsection_info = sections_by_id[`${subsection_id}`];
        if (!subsection_info) return results;
        const subsection_students = Array.isArray(subsection_info.student_list)
          ? subsection_info.student_list.length
          : 0;
        return results + subsection_students;
      }, 0);
      const section_student_count = main_student_count + subsection_student_count;

      if (!fill_totals_by_course_id[course_id_key]) {
        fill_totals_by_course_id[course_id_key] = {
          total_students: 0,
          total_capacity: 0,
        };
      }

      fill_totals_by_course_id[course_id_key].total_students += section_student_count;
      fill_totals_by_course_id[course_id_key].total_capacity += max_class_size;
    });

    return Object.keys(fill_totals_by_course_id).reduce((results, course_id_key) => {
      const totals = fill_totals_by_course_id[course_id_key] || {
        total_students: 0,
        total_capacity: 0,
      };
      const raw_fill_percent =
        totals.total_capacity > 0
          ? Math.round((totals.total_students / totals.total_capacity) * 100)
          : 0;

      results[course_id_key] = {
        raw_fill_percent: raw_fill_percent,
        ring_fill_percent: Math.min(raw_fill_percent, 100),
        total_students: totals.total_students,
        total_capacity: totals.total_capacity,
      };

      return results;
    }, {});
  }, [courses, sections, sectionIsAllowedForCurrentUser, subsectionParentById]);
  const classroomsById = useMemo(
    () =>
      classrooms.reduce((results, classroom) => {
        results[`${classroom.classroom_id}`] = classroom;
        return results;
      }, {}),
    [classrooms]
  );
  const labelsById = useMemo(
    () =>
      labels.reduce((results, label) => {
        if ("label_id" in label) results[`${label.label_id}`] = label;
        return results;
      }, {}),
    [labels]
  );
  const schedulePanelMemo = useMemo(
    () => ({
      scheduleStructureData,
      sections,
      setSections,
      subsections,
      departments,
      studentMatchPercentages,
      schoolInfo,
      scheduleInfo,
      teachers,
      setTeachers,
      courses,
      classrooms,
      students,
      setStudents,
      labels,
      filteredDisplaySchedule,
      courseFillPercentagesByCourseId,
      studentSectionsByStudentCoursePeriod,
      studentConflictSectionLookup,
      sectionConflictSummaryBySectionId,
      sectionsById,
      subsectionParentById,
      teachersById,
      coursesById,
      classroomsById,
      labelsById,
      sectionIsAllowedForCurrentUser,
    }),
    [
      scheduleStructureData,
      sections,
      setSections,
      subsections,
      departments,
      studentMatchPercentages,
      schoolInfo,
      scheduleInfo,
      teachers,
      setTeachers,
      courses,
      classrooms,
      students,
      setStudents,
      labels,
      filteredDisplaySchedule,
      courseFillPercentagesByCourseId,
      studentSectionsByStudentCoursePeriod,
      studentConflictSectionLookup,
      sectionConflictSummaryBySectionId,
      sectionsById,
      subsectionParentById,
      teachersById,
      coursesById,
      classroomsById,
      labelsById,
      sectionIsAllowedForCurrentUser,
    ]
  );
  const requestExportCurrentView = useCallback(async (exportRequestOptions = null) => {
    if(!userCanExportMagnetboardExcel(userInfo)) return;
    if(!exportRequestOptions) {
      setExcelExportOptionsModalVisible(true);
      return;
    }

    setExcelExportOptionsModalVisible(false);

    try {
      const exportInfo = buildMagnetboardExcelExport({
        currentSchedule,
        currentView,
        currentDay,
        currentViewType,
        periodType,
        scheduleType,
        periodsToShow,
        filteredDisplaySchedule,
        scheduleStructureData,
        sections,
        subsections,
        courses,
        teachers,
        classrooms,
        departments,
        sectionSingletonDoubletonMap,
        filterOptions,
        labsOnlyFiltered:labs_only_filtered,
        nonLabsOnlyFiltered:non_labs_only_filtered,
        singletonsFiltered:singletons_filtered,
        doubletonsFiltered:doubletons_filtered,
        hasClassroomFiltered:has_classroom_filtered,
        noClassroomFiltered:no_classroom_filtered,
        exportOptions:exportRequestOptions.exportOptions,
      });
      const xlsxModule = await import("xlsx-js-style");
      const XLSX = xlsxModule.default || xlsxModule;
      const workbook = XLSX.utils.book_new();
      exportInfo.sheets.forEach(sheetInfo => {
        const worksheet = XLSX.utils.aoa_to_sheet(sheetInfo.rows);
        formatMagnetboardExcelWorksheet({
          worksheet,
          rows:sheetInfo.rows,
          columnWidths:sheetInfo.columnWidths,
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetInfo.sheetName);
      });
      XLSX.writeFile(workbook, exportInfo.filename);
    } catch(error) {
      console.log(error);
      swal({
        title:"Unable to Export",
        text:"There was a problem creating the Magnetboard Excel file. Please try again.",
        icon:"error",
      });
    }
  }, [
    classrooms,
    courses,
    departments,
    currentDay,
    currentSchedule,
    currentView,
    currentViewType,
    doubletons_filtered,
    filterOptions,
    filteredDisplaySchedule,
    has_classroom_filtered,
    labs_only_filtered,
    no_classroom_filtered,
    non_labs_only_filtered,
    periodType,
    periodsToShow,
    scheduleStructureData,
    scheduleType,
    sectionSingletonDoubletonMap,
    sections,
    singletons_filtered,
    subsections,
    teachers,
    userInfo,
  ]);
  const updateSectionMoveHistory = useCallback(({
    recordHistory = true,
    clearRedo = true,
    mainSectionId,
    actionView,
    oldCoursePeriodId,
    newCoursePeriodId,
    oldTeacherId,
    newTeacherId,
    oldClassroomId,
    newClassroomId,
    oldCourseId,
    newCourseId,
    oldSecondaryTeachers = [],
    secondaryTeachers = [],
  }) => {
    if (!recordHistory) return;

    const actionLog = getJSONFromStorage("actionLog", true, "session") || [];
    const new_action = {
      action_type: "section_move",
      section_id: mainSectionId,
      current_view: actionView,
      old_section: {
        course_period_id: oldCoursePeriodId,
        teacher_id: oldTeacherId,
        classroom_id: oldClassroomId,
        course_id: oldCourseId,
        old_secondary_teachers: oldSecondaryTeachers,
      },
      new_section: {
        course_period_id: newCoursePeriodId,
        teacher_id: newTeacherId,
        classroom_id: newClassroomId,
        course_id: newCourseId,
        secondary_teachers: secondaryTeachers,
      },
    };

    actionLog.push(new_action);
    sessionStorage.setItem("actionLog", JSON.stringify(actionLog));

    if (clearRedo) {
      sessionStorage.setItem("undoneActions", JSON.stringify([]));
    }
  }, []);

  const refreshMagnetboardDataForCourseMove = useCallback(async () => {
    const [loadedData, sectionData] = await Promise.all([
      loadData({
        data_types: ["students", "teachers", "courses"],
        schedule_version_id: schoolInfo.current_schedule_version_id,
        data_version_id: schoolInfo.current_data_version_id,
        school_id: schoolInfo.school_id,
      }),
      getData('sections', '/get-sections', {school_id: schoolInfo.school_id, schedule_version_id: schoolInfo.current_schedule_version_id}, true),
    ]);

    if (loadedData?.students) setStudents([...loadedData.students]);
    if (loadedData?.teachers) setTeachers([...loadedData.teachers]);
    if (loadedData?.courses) setCourses([...loadedData.courses]);

    if (sectionData) {
      setLabels(sectionData.labels || []);
      setSubsections(sectionData.subsections || []);
      setSections(sectionData.sections || []);
    }
  }, [
    schoolInfo.current_data_version_id,
    schoolInfo.current_schedule_version_id,
    schoolInfo.school_id,
    setCourses,
    setLabels,
    setSections,
    setStudents,
    setSubsections,
    setTeachers,
  ]);

  const saveCourseViewSectionMove = useCallback(async ({
    mainSectionId,
    mainSectionInfo,
    oldCourseId,
    newCourseId,
    oldCoursePeriodId,
    newCoursePeriodId,
    oldTeacherId,
    oldClassroomId,
    oldSecondaryTeachers = [],
    recordHistory = true,
    clearRedo = true,
  }) => {
    const affectedSections = mainSectionInfo.subsections.length > 0
      ? calculateAffectedSectionsForPlacements({
          sections: sections,
          subsections: subsections,
          section_id: mainSectionId,
          new_course_period_id: newCoursePeriodId,
          old_course_period_id: oldCoursePeriodId,
        })
      : [
          {
            section_id: mainSectionId,
            new_course_period_id: newCoursePeriodId,
            old_course_period_id: oldCoursePeriodId,
            edit_teacher: true,
          },
        ];

    const updatedSectionDataById = {};
    const updatedMainSectionInfo = cloneObj(mainSectionInfo);
    updatedMainSectionInfo.course_id = newCourseId;

    [updatedMainSectionInfo, ...mainSectionInfo.subsections.map((subsectionId) => {
      const subsectionInfo = sections.find((section) => section.section_id === subsectionId);
      return subsectionInfo ? cloneObj(subsectionInfo) : null;
    }).filter(Boolean)].forEach((sectionInfo) => {
      updatedSectionDataById[sectionInfo.section_id] = sectionInfo;
    });

    affectedSections.forEach((affectedSection) => {
      const currentSectionInfo = updatedSectionDataById[affectedSection.section_id];
      if (!currentSectionInfo) return;

      if (currentSectionInfo.is_lab !== "1") {
        const sectionQuarterDays = Array.isArray(currentSectionInfo.quarter_days) ? currentSectionInfo.quarter_days : [];
        const quartersToAdd = sectionQuarterDays.reduce((results, quarterDay) => {
          if (!results.includes(quarterDay.quarter)) results.push(quarterDay.quarter);
          return results;
        }, []);

        const daysForCoursePeriodId =
          scheduleStructureData.course_periods[affectedSection.new_course_period_id]?.days || {};
        const daysToAdd = Object.keys(daysForCoursePeriodId).reduce((results, day) => {
          const schedulePeriodOnDay = daysForCoursePeriodId[day];
          if (schedulePeriodOnDay && !results.includes(day)) results.push(day);
          return results;
        }, []);

        currentSectionInfo.quarter_days = [];
        daysToAdd.forEach((day) => {
          quartersToAdd.forEach((quarter) => {
            currentSectionInfo.quarter_days.push({ day: day, quarter: quarter });
          });
        });
      }

      const currentCoursePeriods = Array.isArray(currentSectionInfo.course_periods)
        ? [...currentSectionInfo.course_periods]
        : [];
      const coursePeriodIndex = currentCoursePeriods.findIndex((coursePeriodId) => coursePeriodId === affectedSection.old_course_period_id);

      if (coursePeriodIndex !== -1) {
        currentCoursePeriods[coursePeriodIndex] = affectedSection.new_course_period_id;
      } else if (!currentCoursePeriods.includes(affectedSection.new_course_period_id)) {
        currentCoursePeriods.push(affectedSection.new_course_period_id);
      }

      currentSectionInfo.course_periods = currentCoursePeriods;
    });

    const buildSectionPayload = (sectionInfo) => {
      const sectionStudentList = Array.isArray(sectionInfo.student_list) ? sectionInfo.student_list : [];
      const lockedStudents = students.reduce((results, student) => {
        const studentSections = Array.isArray(student.student_locked_sections) ? student.student_locked_sections : [];
        if (studentSections.includes(sectionInfo.section_id) && sectionStudentList.includes(student.student_id)) {
          results.push(student.student_id);
        }
        return results;
      }, []);

      return {
        ...sectionInfo,
        locked_students: lockedStudents,
      };
    };

    const sectionData = {
      main: buildSectionPayload(updatedMainSectionInfo),
    };

    mainSectionInfo.subsections.forEach((subsectionId) => {
      const subsectionInfo = updatedSectionDataById[subsectionId];
      if (!subsectionInfo) return;
      sectionData[subsectionId] = buildSectionPayload(subsectionInfo);
    });

    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = '/update-section';
    const myInit = {
      response: true,
      body: JSON.stringify({
        school_id: schoolInfo.school_id,
        schedule_version_id: currentScheduleVersionID,
        section_data: sectionData,
        section_type: "1",
      }),
    };

    const result = await API.post(apiName, url, myInit);
    const responseData = result.data;

    if (responseData && responseData.success === false) {
      throw new Error(responseData.msg || "Unable to save section move.");
    }

    updateSectionMoveHistory({
      recordHistory,
      clearRedo,
      mainSectionId,
      actionView: "course",
      oldCoursePeriodId,
      newCoursePeriodId,
      oldTeacherId,
      newTeacherId: oldTeacherId,
      oldClassroomId,
      newClassroomId: oldClassroomId,
      oldCourseId,
      newCourseId,
      oldSecondaryTeachers,
      secondaryTeachers: oldSecondaryTeachers,
    });

    await refreshMagnetboardDataForCourseMove();

    const conflicts_to_check = [
      CONFLICT_TYPES.TEACHER_DOUBLE_BOOKED,
      CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
      CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
      CONFLICT_TYPES.TEACHER_CONFLICTING_PERIODS,
      CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
      CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
      CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION,
      CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS_IN_A_ROW,
      CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION,
      CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
      CONFLICT_TYPES.TEACHER_MISSING,
      CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
      CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
      CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
      CONFLICT_TYPES.CLASSROOM_MISSING,
      CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
      CONFLICT_TYPES.INCLUSION_OVER_HALF,
    ];

    calculateConflicts({
      school_id: schoolInfo.school_id, 
      schedule_version_id: schoolInfo.current_schedule_version_id, 
      conflicts_to_check: conflicts_to_check, 
      match_percent: true
    }).then(conflicts_response => {
      const conflicts_list = conflicts_response.conflicts;
      const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
      setConflicts([...filtered_out_conflicts, ...conflicts_list]);
      markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
      setMatchPercent(conflicts_response.match_percent);
      setConflictsLoading(false);
      setMatchPercentLoading(false);
    });
  }, [
    conflicts,
    currentScheduleVersionID,
    markConflictDataFresh,
    refreshMagnetboardDataForCourseMove,
    schoolInfo.current_schedule_version_id,
    schoolInfo.school_id,
    scheduleStructureData.course_periods,
    sections,
    setConflicts,
    setConflictsLoading,
    setMatchPercent,
    setMatchPercentLoading,
    students,
    subsections,
    updateSectionMoveHistory,
  ]);

  // Functions
  const updateSectionPlacement = useCallback(
    async (item, options = {}) => {
      const { recordHistory = true, clearRedo = true, viewOverride = null } = options;
      const actionView = viewOverride || currentView;

      // This is the original teacher or classroom id that section came from
      const original_data_id = item.draggedDataID;

      // This is the new teacher or classroom id that section was dropped
      const new_data_id = item.droppedDataID;

      // This is the course period where section was dropped
      const new_course_period_id = item.droppedCoursePeriodID;

      // This is the course period where section was dragged from
      const old_course_period_id = item.draggedCoursePeriodID;

      // Sections from magnetboard always have the main section ID attached
      const main_section_id = item.sectionID;
      const main_section_info = sections.find(
        (section) => section.section_id === main_section_id
      );
      if (!sectionIsAllowedForCurrentUser(main_section_info)) return;
      const main_section_subsections = main_section_info.subsections;

      // Now check if dragged section is actually a subsection
      // The only way to check if dragged section is from main teacher or subsection teacher is to compare original data id to subsection teachers
      let affected_section_id = main_section_id;
      
      if (actionView === "teacher") {
        for (const subsection_id of main_section_subsections) {
          const subsection_info = sections.find(
            (subsection) => subsection.section_id === subsection_id
          );
          const subsection_teacher_id = subsection_info?.teacher_id;
          
          if (original_data_id === subsection_teacher_id) {
            affected_section_id = subsection_id;
            break;
          }
        }
      }

      const section_info = sections.find(
        (section) => section.section_id === affected_section_id
      );
      const old_course_periods = section_info.course_periods;
      const old_teacher_id = section_info.teacher_id;
      const old_classroom_id = section_info.classroom_id;
      const old_course_id = section_info.course_id;

      const new_teacher_id =
        actionView === "teacher" ? new_data_id : section_info.teacher_id;
      const new_classroom_id =
        actionView === "classroom" ? new_data_id : section_info.classroom_id;
      const new_course_id =
        actionView === "course" ? new_data_id : section_info.course_id;

      if (old_course_periods.includes(new_course_period_id)) {
        const same_teacher_same_period = (actionView === "teacher" && old_teacher_id === new_teacher_id);
        const same_classroom_same_period = (actionView === "classroom" && old_classroom_id === new_classroom_id);
        const same_course_same_period = (actionView === "course" && old_course_id === new_course_id);
        
        // Only block if it's the same teacher/classroom AND same period (no actual change)
        if (same_teacher_same_period || same_classroom_same_period || same_course_same_period) {
          return;
        }
      }

      // If section was dropped in the same period, do nothing
      const nothing_changed =
        (actionView === "teacher" &&
          old_teacher_id === new_teacher_id &&
          old_course_period_id === new_course_period_id) ||
        (actionView === "classroom" &&
          old_classroom_id === new_classroom_id &&
          old_course_period_id === new_course_period_id) ||
        (actionView === "course" &&
          old_course_id === new_course_id &&
          old_course_period_id === new_course_period_id);
      if (nothing_changed) return;

      const previous_sections = cloneObj(sections);
      const previous_action_log = getJSONFromStorage("actionLog", true, "session") || [];
      const previous_undone_actions = getJSONFromStorage("undoneActions", true, "session") || [];

      setConflictsLoading(true);
      setMatchPercentLoading(true);

      if (actionView === "course" && old_course_id !== new_course_id) {
        try {
          await saveCourseViewSectionMove({
            mainSectionId: main_section_id,
            mainSectionInfo: main_section_info,
            oldCourseId: old_course_id,
            newCourseId: new_course_id,
            oldCoursePeriodId: old_course_period_id,
            newCoursePeriodId: new_course_period_id,
            oldTeacherId: old_teacher_id,
            oldClassroomId: old_classroom_id,
            oldSecondaryTeachers: section_info.secondary_teachers || [],
            recordHistory,
            clearRedo,
          });
        } catch (error) {
          console.log(error);
          setConflictsLoading(false);
          setMatchPercentLoading(false);

          swal({
            title: "Unable to Save",
            icon: "warning",
            buttons: {
              cancel: {
                text: "Okay",
                value: false,
                visible: true,
                className: 'blue-btn'
              }
            },
            content: (
              <div>
                <p>{error?.message || "There was a problem saving this section move."}</p>
              </div>
            )
          });
        }

        return;
      }

      // Check if this section meets any of the requirements to calculate affected sections
      const has_other_affected_sections = main_section_subsections.length > 0;

      ////////////////////////////
      ///// SAVE TO FRONT END ////
      ////////////////////////////
      const affected_sections = has_other_affected_sections
        ? calculateAffectedSectionsForPlacements({
            sections: sections,
            subsections: subsections,
            section_id: affected_section_id,
            new_course_period_id: new_course_period_id,
            old_course_period_id: old_course_period_id,
          })
        : [
            {
              section_id: affected_section_id,
              new_course_period_id: new_course_period_id,
              old_course_period_id: old_course_period_id,
              edit_teacher: true,
            },
          ];
      const affected_section_ids = [];

      let teacher_id = new_teacher_id;
      let old_secondary_teachers = section_info.secondary_teachers || [];
      let secondary_teachers = section_info.secondary_teachers || [];

      const saveAffectedSections = [...affected_sections];

      // Update FRONTEND
      for (const affected_section_index in affected_sections) {
        const affected_section = affected_sections[affected_section_index];
        const section_id = affected_section.section_id;
        const section_index = sections.findIndex(
          (section) => section.section_id === section_id
        );

        if (section_index === -1) return;
        affected_section_ids.push(section_id); // collect section ids to calculate conflicts later

        const section_info = sections[section_index];
        const section_is_lab = section_info.is_lab === "1";

        const edit_teacher = affected_section.edit_teacher;
        const new_course_period_id = affected_section.new_course_period_id;
        const old_course_period_id = affected_section.old_course_period_id;

        // Set current secondary teachers
        saveAffectedSections[affected_section_index].secondary_teachers = sections[section_index].secondary_teachers;

        const isOnOtherAffectedSections = actionView === "teacher" && affected_sections.findIndex(affected_section => {
          const affected_section_id = affected_section.section_id;
          // if same section id, skip
          if (affected_section_id === section_id) return false;
          const affected_section_info = sections.find(section => section.section_id === affected_section_id);
          if (!affected_section_info) return false;
          // if same teacher id, return true
          if (affected_section_info.teacher_id === new_data_id) return true;
          if (affected_section_info.secondary_teachers && affected_section_info.secondary_teachers.length > 0 && affected_section_info.secondary_teachers.includes(new_data_id)) return true;
          return false;
        }) !== -1;

        const isSecondaryTeacher = actionView === "teacher" && section_info.secondary_teachers && section_info.secondary_teachers.length > 0 && section_info.secondary_teachers.includes(new_data_id);

        const isMainSectionTeacher = actionView === "teacher" && section_info.teacher_id === new_data_id;
        const isSubsection = subsections.some(
          (subsection_info) => `${subsection_info.subsection_id}` === `${section_id}`
        );

        if (actionView === "classroom" && original_data_id !== new_data_id) {
          if (!isSubsection || `${section_info.has_separate_classroom || "0"}` !== "1") {
            sections[section_index].classroom_id = new_classroom_id;
            saveAffectedSections[affected_section_index].classroom_id = new_classroom_id;
          }
        } else if (actionView === "teacher" && original_data_id !== new_data_id && !isOnOtherAffectedSections && !isSecondaryTeacher && !isMainSectionTeacher && edit_teacher) {
          // if move secondary teacher card = replace the secondary teacher id in the array
          if (section_info.secondary_teachers && section_info.secondary_teachers.length > 0 && section_info.secondary_teachers.includes(original_data_id)) {
            // replace secondary_teachers for the new
            secondary_teachers = sections[section_index].secondary_teachers.map(teacher_id => {
              if (teacher_id === original_data_id) {
                return new_data_id;
              }
              return teacher_id;
            });
            sections[section_index].secondary_teachers = secondary_teachers;
            saveAffectedSections[affected_section_index].secondary_teachers = secondary_teachers;
          }
          // if move main teacher card = change teacher id
          else if (section_info.teacher_id === original_data_id) {
            sections[section_index].teacher_id = new_data_id;
            saveAffectedSections[affected_section_index].teacher_id = new_data_id;
          }
        }

        // Get new quarter days if section isn't lab
        if (!section_is_lab) {
          const section_quarter_days = sections[section_index].quarter_days;
          const quarters_to_add = section_quarter_days.reduce(
            (results, quarter_day) => {
              const quarter = quarter_day.quarter;
              if (!results.includes(quarter)) results.push(quarter);
              return results;
            },
            []
          );

          const days_for_course_period_id =
            scheduleStructureData.course_periods[new_course_period_id].days;
          const days_to_add = Object.keys(days_for_course_period_id).reduce(
            (results, day) => {
              const schedule_period_on_day = days_for_course_period_id[day];
              if (schedule_period_on_day && !results.includes(day))
                results.push(day);
              return results;
            },
            []
          );

          let new_quarter_days = [];
          for (const day of days_to_add) {
            for (const quarter of quarters_to_add) {
              new_quarter_days.push({ day: day, quarter: quarter });
            }
          }

          if(section_info.has_separate_quarter_days !== '1') sections[section_index].quarter_days = new_quarter_days;
        }

        // Update section course period id
        const course_period_index = sections[
          section_index
        ].course_periods.findIndex((cp) => cp === old_course_period_id);
        if (course_period_index === -1) {
          if (!sections[section_index].course_periods.includes(new_course_period_id)) {
            sections[section_index].course_periods.push(new_course_period_id);
          }
        } else {
          sections[section_index].course_periods[course_period_index] =
            new_course_period_id;
        }
      }

      setSections([...sections]);

      ///////////////////////////////////
      //// SAVE ACTION FOR UNDO/REDO ////
      ///////////////////////////////////
      // Save old version of section to session storage
      updateSectionMoveHistory({
        recordHistory,
        clearRedo,
        mainSectionId: main_section_id,
        actionView,
        oldCoursePeriodId: old_course_period_id,
        newCoursePeriodId: new_course_period_id,
        oldTeacherId: old_teacher_id,
        newTeacherId: teacher_id,
        oldClassroomId: old_classroom_id,
        newClassroomId: new_classroom_id,
        oldCourseId: old_course_id,
        newCourseId: new_course_id,
        oldSecondaryTeachers: old_secondary_teachers,
        secondaryTeachers: secondary_teachers,
      });

      ////////////////////////////
      ///// SAVE TO DATABASE /////
      ////////////////////////////
      // SAVE SECTION PLACEMENT

      try {
        const response_data = await saveSectionPlacement({
          school_id: schoolInfo.school_id,
          schedule_version_id: currentScheduleVersionID,
          department_id: magnetboardOptions.current_department_id,
          subscreen: magnetboardOptions.progress_subscreen,
          teacher_id: teacher_id,
          classroom_id: new_classroom_id,
          affected_sections: saveAffectedSections,
        });

        if (
          response_data &&
          (response_data.success === false ||
            response_data.status === "error" ||
            response_data.msg ||
            response_data.message)
        ) {
          throw new Error(response_data.msg || response_data.message || "Unable to save section move.");
        }
        notifyPlacementChangeSaved({change_type: "section_move", section_id: main_section_id});
      } catch (error) {
        setSections(previous_sections);
        if (recordHistory) {
          sessionStorage.setItem("actionLog", JSON.stringify(previous_action_log));
          sessionStorage.setItem("undoneActions", JSON.stringify(previous_undone_actions));
        }
        setConflictsLoading(false);
        setMatchPercentLoading(false);

        swal({
          title: "Unable to Save",
          icon: "warning",
          buttons: {
            cancel: {
              text: "Okay",
              value: false,
              visible: true,
              className: "blue-btn"
            }
          },
          content: (
            <div>
              <p>{error?.response?.data?.msg || error?.response?.data?.message || error?.message || "There was a problem saving this section move."}</p>
            </div>
          )
        });
        return;
      }

      // Update CONFLICTS
      const conflicts_to_check = [
        CONFLICT_TYPES.TEACHER_DOUBLE_BOOKED,
        CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
        CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
        CONFLICT_TYPES.TEACHER_CONFLICTING_PERIODS,
        CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
        CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
        CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION,
        CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS_IN_A_ROW,
        CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION,
        CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
      ];
      
      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        conflicts_to_check: conflicts_to_check,
        match_percent: true,
      }).then((conflicts_response) => {
        const conflicts_list = conflicts_response.conflicts;
        const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
        setConflicts([...filtered_out_conflicts, ...conflicts_list]);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
        setMatchPercent(conflicts_response.match_percent);
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      });
    },
    [
      currentScheduleVersionID,
      currentView,
      magnetboardOptions.current_department_id,
      magnetboardOptions.progress_subscreen,
      markConflictDataFresh,
      notifyPlacementChangeSaved,
      saveCourseViewSectionMove,
      scheduleStructureData,
      sectionIsAllowedForCurrentUser,
      schoolInfo,
      sections,
      setSections,
      subsections,
      conflicts,
      setConflicts,
      setConflictsLoading,
      setMatchPercent,
      setMatchPercentLoading,
      updateSectionMoveHistory
    ]
  );

  const updateManualPlacement = useCallback(async (props, options = {}) => {
    const { recordHistory = true, clearRedo = true } = options;
    const update_type = props.updateType;
    const section_id = props.sectionID;
    const new_course_period_id = (update_type === 'add') ? props.droppedCoursePeriodID : null;

    const new_course_period_days = (update_type === 'add') ? scheduleStructureData.course_periods[new_course_period_id].days : {};

    // Get the current day or if that doesn't exist, use first day that this course period shows up
    const current_day = (currentDay) ? currentDay : Object.keys(new_course_period_days).reduce((results, day_number) => {
      const day_schedule_period_id = new_course_period_days[day_number];
      if(!results && day_schedule_period_id) results = day_number;
      return results;
    }, null);

    ////////////////////////
    /// Update front end ///
    ////////////////////////
    const section_index = sections.findIndex(section => section.section_id === section_id);
    const section_info = sections[section_index];
    if (!sectionIsAllowedForCurrentUser(section_info)) return;
    const previous_sections = cloneObj(sections);
    const previous_action_log = getJSONFromStorage("actionLog", true, "session") || [];
    const previous_undone_actions = getJSONFromStorage("undoneActions", true, "session") || [];

    setMatchPercentLoading(true);
    setConflictsLoading(true);

    const old_course_period_id = props.draggedCoursePeriodID || (section_info && section_info.course_periods && section_info.course_periods.length > 0 ? section_info.course_periods[0] : null);
    const section_course_id = section_info.course_id;
    const section_semester = section_info.semester;
    const section_span_id = section_info.span_id;
    const section_subsections = section_info.subsections;

    // Collect all the affected main sections
    const lab_sections = (!section_span_id) ? [] : sections.filter(section => section.span_id === section_span_id && section.section_id !== section_id);

    // Get dropped sections new schedule period's order
    const new_schedule_period_order = (update_type === 'add') ? parseInt(scheduleStructureData.schedule_structure[current_day].find(day_ss => day_ss.course_period_id === new_course_period_id).period_order) : null;

    // Keep track of which course periods have been used
    // Add the new course period as logged
    let is_manual = '0';
    let new_course_periods = [];
    let new_quarter_days = [];
    let new_lab_course_periods = [];
    let new_lab_quarter_days = [];
    
    if(update_type === 'add')
    {
      // Update is_manual
      is_manual = '1';

      // Add dropped course period id to the course period list
      let current_period_order = new_schedule_period_order;
      let logged_period_orders = (update_type === 'add') ? [new_schedule_period_order] : [];
      
      new_course_periods.push(new_course_period_id);

      // Const used to help decided quarter days
      const semester_to_quarters = {'FY': ['1', '2', '3', '4'], 'S1':['1','2'], 'S2':['3','4'], 'Q1':['1'], 'Q2':['2'], 'Q3':['3'], 'Q4':['4']};

      // Get schedule period with the max period order
      // Used later to see if we've bumped into the last period of the day
      const max_period_order = scheduleStructureData.schedule_structure[current_day].reduce((results, day_ss_row) => {
        const period_order = parseInt(day_ss_row.period_order);
        if(period_order > results) results = period_order;
        return results;
      }, -1);
      const max_num_schedule_periods = scheduleStructureData.schedule_structure[current_day].length;

      // Figure out if we need to add more course periods
      const section_course_info = courses.find(course => course.course_id === section_course_id);
      const num_periods_spanned = (section_course_info) ? parseInt(section_course_info.num_periods_spanned) : 1;

      for(let i = 1; i < num_periods_spanned; i++)
      {
        let index = 0;
        let section_new_course_period_id = null;

        while(!section_new_course_period_id && index < 5)
        {
          // If we've used every period once, then clear logged period orders
          if(logged_period_orders.length === max_num_schedule_periods) 
          {
            logged_period_orders = [];
            current_period_order = new_schedule_period_order;
          }
  
          // If we haven't reached max period order, then keep incrementing up
          // If we have hit max period order, increment down
          current_period_order = (!logged_period_orders.includes(max_period_order)) ? ++current_period_order: --current_period_order;
  
          // If period order hasn't been used yet, then find course period during this schedule period
          if(!logged_period_orders.includes(current_period_order)) 
          {
            // First find schedule period with this period order
            let temp_day_ss_row = null;
            for(const day_ss_row of scheduleStructureData.schedule_structure[current_day])
            {
              const period_order = parseInt(day_ss_row.period_order);
              if(period_order === current_period_order)
              {
                temp_day_ss_row = day_ss_row;
                break;
              }
            }
  
            // Then find course period id in this schedule period on current day
            section_new_course_period_id = (temp_day_ss_row) ? temp_day_ss_row.course_period_id : null;
  
            // Log the use of this period order
            logged_period_orders.push(current_period_order);
            if(section_new_course_period_id) new_course_periods.push(section_new_course_period_id);
          }
          index++;
        }
      }

      // Find quarters and days to add for this new course period id
      const quarters_to_add = (section_semester in semester_to_quarters) ? semester_to_quarters[section_semester] : [];
      
      // Find days for this course period id
      const days_to_add = new_course_periods.reduce((results, new_course_period_id) => {
        const days_for_cp = scheduleStructureData.course_periods[new_course_period_id].days;

        for(const day in days_for_cp)
        {
          const schedule_period_on_day = days_for_cp[day];
          if(schedule_period_on_day && !results.includes(day)) results.push(day);
        }

        return results;
      },[]);

      // Get new quarter days
      for(const day of days_to_add)
      {
        for(const quarter of quarters_to_add)
        {
          new_quarter_days.push({day:day, quarter:quarter});
        }
      }

      if(lab_sections.length > 0)
      {
        let index = 0;
        let lab_new_course_period_id = null;

        while(!lab_new_course_period_id && index < 5)
        {
          // If we've used every period once, then clear logged period orders
          if(logged_period_orders.length === max_num_schedule_periods) 
          {
            logged_period_orders = [];
            current_period_order = new_schedule_period_order;
          }
  
          // If we haven't reached max period order, then keep incrementing up
          // If we have hit max period order, increment down
          current_period_order = (!logged_period_orders.includes(max_period_order)) ? ++current_period_order: --current_period_order;
  
          // If period order hasn't been used yet, then find course period during this schedule period
          if(!logged_period_orders.includes(current_period_order)) 
          {
            // First find schedule period with this period order
            let temp_day_ss_row = null;
            for(const day_ss_row of scheduleStructureData.schedule_structure[current_day])
            {
              const period_order = parseInt(day_ss_row.period_order);
              if(period_order === current_period_order)
              {
                temp_day_ss_row = day_ss_row;
                break;
              }
            }
  
            // Then find course period id in this schedule period on current day
            lab_new_course_period_id = (temp_day_ss_row) ? temp_day_ss_row.course_period_id : null;
  
            // Log the use of this period order
            logged_period_orders.push(current_period_order);
            if(lab_new_course_period_id) new_lab_course_periods.push(lab_new_course_period_id);
          }
          index++;
        }
      }
    }

    // Set new section course periods and quarter days
    // If this was adding, these values are caluclated above
    // If this is removing, these should be empty arrays
    sections[section_index].course_periods = new_course_periods;
    sections[section_index].quarter_days = new_quarter_days;
    sections[section_index].is_manual = is_manual;

    section_subsections.forEach(subsection_id => {
      const subsection_section_index = sections.findIndex(section => section.section_id === subsection_id);
      if(subsection_section_index !== -1) 
      {
        sections[subsection_section_index].course_periods = new_course_periods;
        if(sections[subsection_section_index].has_separate_quarter_days !== '1') sections[subsection_section_index].quarter_days = new_quarter_days;
        sections[subsection_section_index].is_manual = is_manual;
      }
    });

    // Add new lab course periods and quarter days (if course has lab sections)
    for(const section_info of lab_sections)
    {
      const lab_section_id = section_info.section_id;
      const lab_subsections = section_info.subsections;
      const lab_section_index = sections.findIndex(section => section.section_id === lab_section_id);

      sections[lab_section_index].course_periods = new_lab_course_periods;
      sections[lab_section_index].quarter_days = new_lab_quarter_days;
      sections[lab_section_index].is_manual = is_manual;

      lab_subsections.forEach(subsection_id => {
        const subsection_section_index = sections.findIndex(section => section.section_id === subsection_id);
        if(subsection_section_index !== -1) 
        {
          sections[subsection_section_index].course_periods = new_lab_course_periods;
          if(sections[subsection_section_index].has_separate_quarter_days !== '1') sections[subsection_section_index].quarter_days = new_lab_quarter_days;
          sections[subsection_section_index].is_manual = is_manual;
        }
      });
    }

    setSections([...sections]);

    if (recordHistory) {
      const actionLog = getJSONFromStorage("actionLog", true, "session") || [];
      const new_action = {
        action_type: "manual_placement_move",
        section_id: section_id,
        current_view: currentView,
        from_course_period_id: old_course_period_id,
        to_course_period_id: new_course_period_id,
      };

      actionLog.push(new_action);
      sessionStorage.setItem("actionLog", JSON.stringify(actionLog));

      if (clearRedo) {
        sessionStorage.setItem("undoneActions", JSON.stringify([]));
      }
    }

    ////////////////////////
    /// Update back end  ///
    ////////////////////////

    const data = {school_id: schoolInfo.school_id, schedule_version_id:currentScheduleVersionID, department_id:magnetboardOptions.current_department_id, update_type:update_type, section_info:section_info, new_course_period_id:new_course_period_id, current_day:current_day};
  
    try {
      const response_data = await updateManualPlacementAPI(data);

      if (
        response_data &&
        (response_data.success === false ||
          response_data.status === "error" ||
          response_data.msg ||
          response_data.message)
      ) {
        throw new Error(response_data.msg || response_data.message || "Unable to save manual placement.");
      }
      const returned_sections = response_data.returned_sections;

      // Update sections
      const affected_section_ids = [];
      returned_sections.forEach(changed_section_info => {
        const section_id = changed_section_info.section_id;
        const new_course_periods = changed_section_info.course_periods;
        const quarter_days = ('quarter_days' in changed_section_info) ? changed_section_info.quarter_days : null;

        // See if previous section id existed
        const previous_section_index = sections.findIndex(section => section.section_id === section_id);

        // If section existed, replace old section data
        if(previous_section_index !== -1) 
        {
          sections[previous_section_index].course_periods = new_course_periods;
          if (update_type === 'undo') {
            sections[previous_section_index].classroom_id = null;
          }
          if(quarter_days) sections[previous_section_index].quarter_days = quarter_days;
        }

        affected_section_ids.push(section_id);
      });

      setSections([...sections]);

      notifyPlacementChangeSaved({ change_type: "manual_placement", update_type, section_id });

      // Update conflicts
      const conflicts_to_check = [
        CONFLICT_TYPES.TEACHER_DOUBLE_BOOKED,
        CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
        CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
        CONFLICT_TYPES.TEACHER_CONFLICTING_PERIODS,
        CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
        CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
        CONFLICT_TYPES.TEACHER_MISSING,
        CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION,
        CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS,
        CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS_IN_A_ROW,
        CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
        CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
        CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION,
        CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
        CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
        CONFLICT_TYPES.CLASSROOM_MISSING
      ];

      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: currentScheduleVersionID,
        conflicts_to_check: conflicts_to_check,
        match_percent: true
      }).then(conflicts_response => {
        const conflicts_list = conflicts_response.conflicts;
        const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
        setConflicts([...filtered_out_conflicts, ...conflicts_list]);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
        setMatchPercent(conflicts_response.match_percent);
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      });
    } catch(error)
    {
      setSections(previous_sections);
      if (recordHistory) {
        sessionStorage.setItem("actionLog", JSON.stringify(previous_action_log));
        sessionStorage.setItem("undoneActions", JSON.stringify(previous_undone_actions));
      }
      setConflictsLoading(false);
      setMatchPercentLoading(false);

      swal({
        title: "Unable to Save",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Okay",
            value: false,
            visible: true,
            className: "blue-btn"
          }
        },
        content: (
          <div>
            <p>{error?.response?.data?.msg || error?.response?.data?.message || error?.message || "There was a problem saving this section placement."}</p>
          </div>
        )
      });
    }
  }, [
    courses,
    currentView,
    currentScheduleVersionID,
    markConflictDataFresh,
    magnetboardOptions.current_department_id,
    notifyPlacementChangeSaved,
    scheduleStructureData,
    schoolInfo.school_id,
    sections,
    setSections,
    setConflicts,
    setConflictsLoading,
    setMatchPercent,
    setMatchPercentLoading,
    sectionIsAllowedForCurrentUser,
    currentDay
  ]);

  const undoAction = useCallback(() => {
    const undoActionAsync = async () => {
      const actionLog = getJSONFromStorage('actionLog', true, 'session') || [];
      
      if(actionLog.length === 0) return false;

      const last_action = actionLog.pop();
      const action_type = last_action.action_type || "section_move";
      const section_id = last_action.section_id;
      const current_view = last_action.current_view;

      if (action_type === "manual_placement_move") {
        const from_course_period_id = last_action.from_course_period_id || null;

        if (from_course_period_id) {
          await updateManualPlacement(
            {
              updateType: "add",
              sectionID: section_id,
              droppedCoursePeriodID: from_course_period_id,
              draggedCoursePeriodID: null,
            },
            { recordHistory: false, clearRedo: false }
          );
        } else {
          await updateManualPlacement(
            {
              updateType: "undo",
              sectionID: section_id,
              droppedCoursePeriodID: null,
              draggedCoursePeriodID: null,
            },
            { recordHistory: false, clearRedo: false }
          );
        }
      } else {
        // New section info (aka dragged)
        const new_section_info = last_action.new_section;
        const new_course_period_id = new_section_info.course_period_id;
        const new_teacher_id = new_section_info.teacher_id;
        const new_classroom_id = new_section_info.classroom_id;
        const new_course_id = new_section_info.course_id;

        // Old section info (aka dropped)
        const old_section_info = last_action.old_section;
        const old_course_period_id = old_section_info.course_period_id;
        const old_teacher_id = old_section_info.teacher_id;
        const old_classroom_id = old_section_info.classroom_id;
        const old_course_id = old_section_info.course_id;

        const old_data_id = current_view === 'teacher'
          ? old_teacher_id
          : current_view === 'course'
          ? old_course_id
          : old_classroom_id;
        const new_data_id = current_view === 'teacher'
          ? new_teacher_id
          : current_view === 'course'
          ? new_course_id
          : new_classroom_id;

        // Update section placement with old info
        await updateSectionPlacement(
          {sectionID:section_id, droppedCoursePeriodID:old_course_period_id, draggedCoursePeriodID:new_course_period_id, droppedDataID:old_data_id, draggedDataID:new_data_id},
          { recordHistory: false, clearRedo: false, viewOverride: current_view }
        );
      }

      // Add the last action into redo list
      const undoneActions = getJSONFromStorage('undoneActions', true, 'session') || [];
      undoneActions.push(last_action);

      sessionStorage.setItem('actionLog', JSON.stringify(actionLog));
      sessionStorage.setItem('undoneActions', JSON.stringify(undoneActions));
    };

    undoActionAsync();
  },[updateManualPlacement, updateSectionPlacement])

  const redoAction = useCallback(() => {
    const redoActionAsync = async () => {
      const undoneActions = getJSONFromStorage('undoneActions', true, 'session') || [];

      if(undoneActions.length === 0) return false;

      const last_action = undoneActions.pop();
      const action_type = last_action.action_type || "section_move";
      const section_id = last_action.section_id;
      const current_view = last_action.current_view;

      if (action_type === "manual_placement_move") {
        const to_course_period_id = last_action.to_course_period_id || null;

        if (to_course_period_id) {
          await updateManualPlacement(
            {
              updateType: "add",
              sectionID: section_id,
              droppedCoursePeriodID: to_course_period_id,
              draggedCoursePeriodID: null,
            },
            { recordHistory: false, clearRedo: false }
          );
        } else {
          await updateManualPlacement(
            {
              updateType: "undo",
              sectionID: section_id,
              droppedCoursePeriodID: null,
              draggedCoursePeriodID: null,
            },
            { recordHistory: false, clearRedo: false }
          );
        }
      } else {
        // Old section info (aka dragged)
        const old_section_info = last_action.old_section;
        const old_course_period_id = old_section_info.course_period_id;
        const old_teacher_id = old_section_info.teacher_id;
        const old_classroom_id = old_section_info.classroom_id;
        const old_course_id = old_section_info.course_id;

        // New section info (aka dropped)
        const new_section_info = last_action.new_section;
        const new_course_period_id = new_section_info.course_period_id;
        const new_teacher_id = new_section_info.teacher_id;
        const new_classroom_id = new_section_info.classroom_id;
        const new_course_id = new_section_info.course_id;

        const new_data_id = current_view === 'teacher'
          ? new_teacher_id
          : current_view === 'course'
          ? new_course_id
          : new_classroom_id;
        const old_data_id = current_view === 'teacher'
          ? old_teacher_id
          : current_view === 'course'
          ? old_course_id
          : old_classroom_id;

        // Update section placement with old info
        await updateSectionPlacement(
          {sectionID: section_id, droppedCoursePeriodID:new_course_period_id, draggedCoursePeriodID:old_course_period_id, droppedDataID:new_data_id, draggedDataID:old_data_id},
          { recordHistory: false, clearRedo: false, viewOverride: current_view }
        );
      }

      const actionLog = getJSONFromStorage('actionLog', true, 'session') || [];
      actionLog.push(last_action);

      sessionStorage.setItem('actionLog', JSON.stringify(actionLog));
      sessionStorage.setItem('undoneActions', JSON.stringify(undoneActions));
    };

    redoActionAsync();
  },[updateManualPlacement, updateSectionPlacement])

  useEffect(() => {
    const isEditableElement = (element) => {
      if (!element) return false;
      const tagName = element.tagName ? element.tagName.toLowerCase() : "";
      if (tagName === "input" || tagName === "textarea" || tagName === "select") return true;
      if (element.isContentEditable) return true;
      if (element.closest && element.closest("[contenteditable='true']")) return true;
      return false;
    };

    const handleUndoRedoShortcuts = (event) => {
      if (event.repeat) return;
      if (isEditableElement(event.target)) return;

      const key = (event.key || "").toLowerCase();
      const isMetaPressed = event.metaKey;
      const isCtrlPressed = event.ctrlKey;
      const hasPrimaryModifier = isMetaPressed || isCtrlPressed;

      if (!hasPrimaryModifier) return;

      const isUndo = key === "z" && !event.shiftKey;
      const isRedo =
        key === "y" ||
        (key === "z" && event.shiftKey); // Cmd/Ctrl + Shift + Z fallback

      if (isUndo) {
        event.preventDefault();
        event.stopPropagation();
        undoAction();
      } else if (isRedo) {
        event.preventDefault();
        event.stopPropagation();
        redoAction();
      }
    };

    window.addEventListener("keydown", handleUndoRedoShortcuts);
    return () => window.removeEventListener("keydown", handleUndoRedoShortcuts);
  }, [undoAction, redoAction]);

  const pullNewSectionData = (schedule_version_id, _callback = ()=>{}) => {
    setOtherSchedulesVisible(false);

    const old_schedule_index = scheduleInfo.schedules.findIndex(schedule => schedule.schedule_version_id === currentScheduleVersionID);
    const new_schedule_index = scheduleInfo.schedules.findIndex(schedule => schedule.schedule_version_id === schedule_version_id);

    if(new_schedule_index === -1) return;
    if(old_schedule_index !== -1) scheduleInfo.schedules[old_schedule_index].current_bool = '0';

    scheduleInfo.schedules[new_schedule_index].current_bool = '1';

    setCurrentScheduleVersionID(scheduleInfo.schedules[new_schedule_index].schedule_version_id);
    setCurrentSchedule({...scheduleInfo.schedules[new_schedule_index]});

    getData('sections', '/get-sections', {school_id: schoolInfo.school_id, schedule_version_id:schedule_version_id}, true).then(returned_data => {
      setLabels(returned_data.labels);
      setSubsections(returned_data.subsections);
      setSections(returned_data.sections);
      setIsLoading(false);

      _callback();
    });
  }

  const duplicateSchedule = useCallback(() => {
    if(!handleDuplicateScheduleVersion || !('schedules' in scheduleInfo)) return;

    const current_schedule_info = scheduleInfo.schedules.find(schedule => `${schedule.schedule_version_id}` === `${currentScheduleVersionID}`);
    if(!current_schedule_info) return;

    handleDuplicateScheduleVersion({
      school_id: schoolInfo.school_id,
      schedule_version_id: current_schedule_info.schedule_version_id,
      schedule_year_id: current_schedule_info.schedule_year_id,
      schedule_name: current_schedule_info.schedule_name
    });
  }, [handleDuplicateScheduleVersion, scheduleInfo, currentScheduleVersionID, schoolInfo.school_id]);

  const deleteSchedule = useCallback(() => {
    if(!handleDeleteScheduleVersion || !('schedules' in scheduleInfo)) return;

    const current_schedule_info = scheduleInfo.schedules.find(schedule => `${schedule.schedule_version_id}` === `${currentScheduleVersionID}`);
    if(!current_schedule_info) return;

    handleDeleteScheduleVersion({
      school_id: schoolInfo.school_id,
      schedule_version_id: current_schedule_info.schedule_version_id,
      schedule_year_id: current_schedule_info.schedule_year_id
    });
  }, [handleDeleteScheduleVersion, scheduleInfo, currentScheduleVersionID, schoolInfo.school_id]);

  const clearStudents = async () => {	
    const options =  {
      title: "Are you sure?",
      text: 'This will remove all students from sections that are not locked.',
      icon: "warning",
      dangerMode: true,
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, clear students",
          value: true,
          visible: true,
          className: 'red-btn',
          closeModal: false
        },
      },
      content: (
        <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
          <div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
            <div><strong>Pro Tip:</strong></div>
            <div>If you want to keep students in a certain section, make sure to lock that section (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the section) before clearing!</div>
          </div>
        </div>
      )
    }
    
    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        setMatchPercentLoading(true);
        setConflictsLoading(true);

        // Update front end
        let new_sections = sections.map(section => {
          const locked = section.locked;

          if(locked === '1') return section;

          const section_id = section.section_id;
          const student_list = section.student_list;

          // Update student requests
          const new_student_list = student_list.reduce((results, student_id) => {
            const student_info = students.find(student => student.student_id === student_id);
            if(!student_info) return results;

            const student_locked_sections = student_info.student_locked_sections;
            const student_has_section_locked = student_locked_sections.includes(section_id);
            
            if(student_has_section_locked) results.push(student_id);
            return results;
          }, []);

          section.student_list = new_student_list;
          return section;
        });

        setStudents([...students]);
        setSections([...new_sections]);

        // Send to Backend ///
        const data = {school_id: schoolInfo.school_id, schedule_version_id:currentScheduleVersionID};
        const apiName = process.env.REACT_APP_ENDPOINT_NAME;
        const url = '/clear-students';
        const myInit = { // OPTIONAL
          response: true,
          body: JSON.stringify(data),
        };
      
        try {
          // Clear students
          await API.post(apiName, url, myInit);

          // Update conflicts
          const conflicts_to_check = [
            CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
            CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
            CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
            CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
            CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
            CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
            CONFLICT_TYPES.INCLUSION_OVER_HALF
          ];

          const conflicts_response = await calculateConflicts({
            school_id: schoolInfo.school_id, 
            schedule_version_id: currentScheduleVersionID, 
            conflicts_to_check: conflicts_to_check, 
            match_percent: true
          });
          const conflicts_list = conflicts_response.conflicts;
          const match_percent = conflicts_response.match_percent;
          const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
          setConflicts([...filtered_out_conflicts, ...conflicts_list]);
          markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
          setMatchPercent(match_percent);
          setConflictsLoading(false);
          setMatchPercentLoading(false);
          finishSwalConfirmLoading();
        } catch(e)
        {
          swal.stopLoading();
          setConflictsLoading(false);
          setMatchPercentLoading(false);
          console.log(e.response);
        }
      }
    });
  }

  const placeStudents = async () => {
    const options =  {
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, place students",
          value: true,
          visible: true,
          className: 'blue-btn',
          closeModal: false
        },
      },
      content: (
        <div>
          <FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
          <h1 className='swal-title' style={{marginTop:'0px'}}>Place Students</h1>
          <p className='swal-text'>This will try to place any unmatched students into available sections!</p>
          <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
            <div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
              <div><strong>Pro Tip:</strong></div>
              <div>Students will not get placed in any currently locked sections. If you want to allow students to be placed in a certain section, unlock the section (by clicking the <FontAwesomeIcon className='orange-text' icon={faLock}/> icon on the section) before placing!</div>
            </div>
          </div>
        </div>
        
      )
    }

    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        const placing_in_progress_options =  {
          button:false,
          closeOnClickOutside: false,
          closeOnEsc: false,
          content: (
            <div>
              <FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faCalendarDay}/>
              <h1 className='swal-title' style={{marginTop:'0px'}}>Placing Students in Progress</h1>
              <p className='swal-text'>Please wait, this may take a few minutes...</p>
              <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
                <div className='red-text'>Do not refresh the page until this process is complete!</div>
              </div>
            </div>
          )
        }

        swal(placing_in_progress_options);

        const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_version_id:schoolInfo.current_data_version_id};
        const apiName = process.env.REACT_APP_ENDPOINT_NAME;
        const url = '/place-students';
        const myInit = { // OPTIONAL
          response: true,
          queryStringParameters: data,
        };
      
        try {
          await API.get(apiName, url, myInit);
          
          const fileUpload = new FileUpload(schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id);
          const running_processes = await fileUpload.getSchoolProcesses(schoolInfo.current_schedule_year_id);
  
          // Check if there are any running processes with the same data version id
          const process_index = running_processes.findIndex(process => process.process_type === 'studentplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);
  
          // If process is already running, get progress of matches
          const process_info = (process_index !== -1) ? running_processes[process_index] : null;
          const progress_id = (process_info) ? process_info.progress_id : null;

          await fileUpload.checkUploading(schoolInfo.school_id, progress_id, (event) => {
            if(event.done) 
            {
              if(event.failure) 
              {
                const failure_options =  {
                  icon:"error",
                  title: "There was an error placing students.",
                  text: "Please try again.",
                  dangerMode: true,
                }

                swal(failure_options);
              }
              else 
              {
                setConflictsLoading(true);
                setMatchPercentLoading(true);

                const success_options =  {
                  icon:"success",
                  title: "Success!",
                  text: "Your students should now be placed.",
                }

                pullNewSectionData(schoolInfo.current_schedule_version_id);
                swal(success_options);

                ////////////////////////////
                ///// UPDATE CONFLICTS /////
                ////////////////////////////
                const conflicts_to_check = [
                  CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
                  CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
                  CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
                  CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
                  CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
                  CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
                  CONFLICT_TYPES.INCLUSION_OVER_HALF,
                ];
                
                // Recalculate conflicts
                calculateConflicts({
                  school_id: schoolInfo.school_id, 
                  schedule_version_id: schoolInfo.current_schedule_version_id, 
                  conflicts_to_check: conflicts_to_check, 
                  match_percent: true
                }).then(conflicts_response => {
                  const conflicts_list = conflicts_response.conflicts;
                  const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
                  setConflicts([...filtered_out_conflicts, ...conflicts_list]);
                  markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
                  setConflictsLoading(false);
                  setMatchPercent(conflicts_response.match_percent);
                  setMatchPercentLoading(false);
                });
              }
            }
          });
        } catch(error)
        {
          const failure_options =  {
            icon:"error",
            title: "Error Placing Students",
            text: "We had an issue placing your students. Please try again.",
            dangerMode: true,
          }

          swal(failure_options);
          console.log(error.response);
        }
      }
    });
  }

  const clearSections = async () => {	
    const options =  {
      title: "Are you sure?",
      text: 'This will remove all sections from the schedule (that are not locked). This will NOT remove sections that were manually placed.',
      icon: "warning",
      dangerMode: true,
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, clear sections",
          value: true,
          visible: true,
          className: 'red-btn',
          closeModal: false
        },
      },
      content: (
        <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
          <div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
            <div><strong>Pro Tip:</strong></div>
            <div>If you want to keep certain sections where they are, make sure to lock that section (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the section) before clearing!</div>
          </div>
        </div>
      )
    }
    
    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        setConflictsLoading(true);

        // Update front end
        let new_sections = sections.map(section => {
          const locked = section.locked;
          const is_manual = section.is_manual;

          if(locked === '1' || is_manual === '1') return section;

          const course_id = section.course_id;
          const student_list = section.student_list;
          const section_is_inclusion = `${section.is_inclusion || '0'}` === '1';

          // Update student requests
          student_list.forEach(student_id => {
          const student_index = students.findIndex(student => student.student_id === student_id);

            if(student_index !== -1)
            {
              const student_info = students[student_index];
              const student_requests = student_info.student_requests;

              const request_match = findCourseRequestInStudentRequests(student_requests, course_id, {
                isInclusion: scheduleInfo.separate_ICR_codes === '0' ? section_is_inclusion : null,
              });
              if(request_match) request_match.request_info.matched = '0';
            }
          });

          section.course_periods = [];
          section.quarter_days = [];
          section.student_list = [];
          return section;
        });

        setStudents([...students]);
        setSections([...new_sections]);
        sessionStorage.setItem("actionLog", JSON.stringify([]));
        sessionStorage.setItem("undoneActions", JSON.stringify([]));

        try {
          // Clear sections
          await clearSectionPlacement({school_id: schoolInfo.school_id, schedule_version_id:currentScheduleVersionID});

          // Recalculate conflicts
          const conflicts_to_check = [
            CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
            CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION,
            CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
            CONFLICT_TYPES.TEACHER_MISSING,
            CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION,
            CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
            CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
            CONFLICT_TYPES.TEACHER_DOUBLE_BOOKED,
            CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
            CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
            CONFLICT_TYPES.TEACHER_CONFLICTING_PERIODS,
            CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
            CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
            CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS,
            CONFLICT_TYPES.TEACHER_TOO_MANY_SECTIONS_IN_A_ROW,
            CONFLICT_TYPES.CLASSROOM_MISSING,
            CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
            CONFLICT_TYPES.INCLUSION_OVER_HALF,
          ];
          
          const conflicts_response = await calculateConflicts({
            school_id: schoolInfo.school_id, 
            schedule_version_id: schoolInfo.current_schedule_version_id, 
            conflicts_to_check: conflicts_to_check, 
            match_percent: true
          });
          const conflicts_list = conflicts_response.conflicts;
          const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
          setConflicts([...filtered_out_conflicts, ...conflicts_list]);
          markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
          setConflictsLoading(false);
          setMatchPercent(conflicts_response.match_percent);
          setMatchPercentLoading(false);
          finishSwalConfirmLoading();
        } catch(e)
        {
          swal.stopLoading();
          setConflictsLoading(false);
          setMatchPercentLoading(false);
          console.log(e.response);
        }
      }
    });
  }
  
  const clearClassrooms = async () => {	
    const options =  {
      title: "Are you sure?",
      text: 'This will remove classrooms from all sections in the schedule (that are not locked). This will NOT remove classrooms from sections that were manually placed.',
      icon: "warning",
      dangerMode: true,
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, clear classrooms",
          value: true,
          visible: true,
          className: 'red-btn',
          closeModal: false
        },
      },
      content: (
        <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
          <div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
            <div><strong>Pro Tip:</strong></div>
            <div>If you want to keep classrooms of certain sections the way they are, make sure to lock that section (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the section) before clearing!</div>
          </div>
        </div>
      )
    }
    
    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        setConflictsLoading(true);

        // Update front end
        let new_sections = sections.map(section => {
          const locked = section.locked;
          const is_manual = section.is_manual;

          if(locked === '1' || is_manual === '1') return section;

          section.classroom_id = null;
          return section;
        });

        setSections([...new_sections]);

        // Send to Backend ///
        const data = {school_id: schoolInfo.school_id, schedule_version_id:currentScheduleVersionID};
        const apiName = process.env.REACT_APP_ENDPOINT_NAME;
        const url = '/clear-classrooms';
        const myInit = { // OPTIONAL
          response: true,
          body: JSON.stringify(data),
        };
      
        try {
          // Clear classrooms
          await API.post(apiName, url, myInit);

          // Update conflicts
          const conflicts_to_check = [
            CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
            CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
            CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
            CONFLICT_TYPES.CLASSROOM_MISSING,
          ];
          const conflicts_response = await calculateConflicts({
            school_id: schoolInfo.school_id, 
            schedule_version_id: currentScheduleVersionID, 
            conflicts_to_check: conflicts_to_check
          });
          const conflicts_list = conflicts_response.conflicts;
          const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
          setConflicts([...filtered_out_conflicts, ...conflicts_list]);
          markConflictDataFresh(schoolInfo.school_id, currentScheduleVersionID);
          setConflictsLoading(false);
          finishSwalConfirmLoading();
        } catch(e)
        {
          swal.stopLoading();
          setConflictsLoading(false);
          console.log(e.response);
        }
      }
    });
  }

  const placeClassrooms = async () => {
    const options =  {
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, place classrooms",
          value: true,
          visible: true,
          className: 'blue-btn',
          closeModal: false
        },
      },
      content: (
        <div>
          <FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
          <h1 className='swal-title' style={{marginTop:'0px'}}>Place Classrooms</h1>
          <p className='swal-text'>This will fill empty classrooms for sections that are not locked.</p>
        </div>
      )
    }

    await swal(options).then(async (return_value) => {
      if(!return_value) return;

      await waitForBrowserPaint();
      setClassroomPlacementReport({ loading: true, assigned_count: 0, unplaced_sections: [] });
      addOpenReport("classroom-placement");
      swal.close();
      setConflictsLoading(true);

      try {
        const result = await runClassroomPlacement({school_id: schoolInfo.school_id, schedule_version_id: currentScheduleVersionID});
        if(result?.errorType || result?.errorMessage) throw new Error(result?.errorMessage || "Classroom placement failed.");
        pullNewSectionData(currentScheduleVersionID);

        const conflicts_to_check = [
          CONFLICT_TYPES.COURSE_OUTSIDE_NORMAL_ROOM,
          CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
          CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
          CONFLICT_TYPES.CLASSROOM_MISSING,
        ];

        calculateConflicts({
          school_id: schoolInfo.school_id,
          schedule_version_id: currentScheduleVersionID,
          conflicts_to_check: conflicts_to_check
        }).then(conflicts_response => {
          const conflicts_list = conflicts_response?.conflicts || [];
          const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
          setConflicts([...filtered_out_conflicts, ...conflicts_list]);
          markConflictDataFresh(schoolInfo.school_id, currentScheduleVersionID);
          setConflictsLoading(false);
        }).catch(error => {
          setConflictsLoading(false);
        });

        const assigned_count = Number(result?.assigned_count || 0);
        const unplaced_sections = Array.isArray(result?.unplaced_sections) ? result.unplaced_sections : [];
        const unassigned_count = unplaced_sections.length;

        if(unassigned_count > 0) {
          setClassroomPlacementReport(result);
        }
        else {
          setClassroomPlacementReport(result);
          swal({
            icon:"success",
            title: "Classrooms Placed",
            text: `${assigned_count} classroom${assigned_count === 1 ? '' : 's'} placed.`,
          });
        }
      } catch(error) {
        setConflictsLoading(false);
        setClassroomPlacementReport({
          loading: false,
          error: true,
          message: "We had an issue placing classrooms. Please try again.",
          assigned_count: 0,
          unplaced_sections: [],
        });
        swal({
          icon:"error",
          title: "Error Placing Classrooms",
          text: "We had an issue placing classrooms. Please try again.",
          dangerMode: true,
        });
      }
    });
  }

  const placeSections = async () => {
    const options =  {
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Yes, place sections",
          value: true,
          visible: true,
          className: 'blue-btn',
          closeModal: false
        },
      },
      content: (
        <div>
          <FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
          <h1 className='swal-title' style={{marginTop:'0px'}}>Place Sections</h1>
          <p className='swal-text'>This will try to place all your unplaced sections!</p>
        </div>
        
      )
    }

    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        const placing_in_progress_options =  {
          button:false,
          closeOnClickOutside: false,
          closeOnEsc: false,
          content: (
            <div>
              <FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faCalendarDay}/>
              <h1 className='swal-title' style={{marginTop:'0px'}}>Placing Sections in Progress</h1>
              <p className='swal-text'>Please wait, this may take a few minutes...</p>
              <div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
                <div className='red-text'>Do not refresh the page until this process is complete!</div>
              </div>
            </div>
          )
        }

        swal(placing_in_progress_options);

        try {
          await runSectionPlacement({school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_version_id:schoolInfo.current_data_version_id});
          
          const fileUpload = new FileUpload(schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id);
          const running_processes = await fileUpload.getSchoolProcesses(schoolInfo.current_schedule_year_id);

          // Check if there are any running processes with the same data version id
          const process_index = running_processes.findIndex(process => process.process_type === 'sectionplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);

          // If process is already running, get progress of matches
          const process_info = (process_index !== -1) ? running_processes[process_index] : null;
          const progress_id = (process_info) ? process_info.progress_id : null;

          await fileUpload.checkUploading(schoolInfo.school_id, progress_id, (event) => {
            if(event.done) 
            {
              if(event.failure) 
              {
                const failure_options =  {
                  icon:"error",
                  title: "There was an error placing sections.",
                  text: "Please try again.",
                  dangerMode: true,
                }

                swal(failure_options);
              } 
              else 
              {
                setConflictsLoading(true);
                setMatchPercentLoading(true);

                const success_options =  {
                  icon:"success",
                  title: "Success!",
                  text: "Your sections should now be placed.",
                }

                pullNewSectionData(schoolInfo.current_schedule_version_id);
                swal(success_options);

                ////////////////////////////
                ///// UPDATE CONFLICTS /////
                ////////////////////////////
                const conflicts_to_check = [
                  CONFLICT_TYPES.TEACHER_DOUBLE_BOOKED,
                  CONFLICT_TYPES.TEACHER_CONFLICTING_PERIODS,
                  CONFLICT_TYPES.CLASSROOM_DOUBLE_BOOKED,
                  CONFLICT_TYPES.CLASSROOM_CONFLICTING_PERIODS,
                ];
                
                calculateConflicts({
                  school_id: schoolInfo.school_id, 
                  schedule_version_id: schoolInfo.current_schedule_version_id, 
                  conflicts_to_check: conflicts_to_check, 
                  match_percent: true
                }).then(conflicts_response => {
                  const conflicts_list = conflicts_response.conflicts;
                  const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
                  setConflicts([...filtered_out_conflicts, ...conflicts_list]);
                  markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
                  setConflictsLoading(false);
                  setMatchPercent(conflicts_response.match_percent);
                  setMatchPercentLoading(false);
                });
              }
            }
          });
        } catch(error)
        {
          const failure_options =  {
            icon:"error",
            title: "Error Placing Sections",
            text: "We had an issue placing your sections. Please try again.",
            dangerMode: true,
          }

          swal(failure_options);
          console.log(error.response);
        }
      }
    });
  }

  const visibleCoursePeriodIDs = useMemo(() => new Set(
    periodsToShow
      .map((period_info) =>
        getCoursePeriodIdForPeriod(scheduleStructureData, period_info.period_id, {
          period_type: periodType,
          schedule_type: scheduleType,
          current_day: currentDay,
        })
      )
      .filter(Boolean)
      .map((course_period_id) => `${course_period_id}`)
  ), [currentDay, periodType, periodsToShow, scheduleStructureData, scheduleType]);

  const toggleLockSection = async (section_ids, locked) => {
    setIsLockSectionLoading(true);
    setIsLockSectionIds(section_ids);
    // Collect all affected sections
    let affected_section_ids = [...section_ids];
    for(const section_id of section_ids)
    {
      const section_info = sections.find(section => section.section_id === section_id);
      const section_subsections = (section_info) ? section_info.subsections : [];

      // Add any subsections
      for(const subsection_id of section_subsections)
      {
        if(!affected_section_ids.includes(subsection_id)) affected_section_ids.push(subsection_id);
      }

      // Check if this section has a lab
      // If yes, add lab and all subsections of lab as affected
      const section_span_id = (section_info) ? section_info.span_id : null;
      if(section_span_id)
      {
        const lab_sections = sections.filter(section => section.span_id === section_span_id && section.section_id !== section_id);
        
        affected_section_ids = lab_sections.reduce((results, lab_section) => {
          const lab_section_id = lab_section.section_id;
          const lab_subsections = lab_section.subsections;

          if(!results.includes(lab_section_id)) results.push(lab_section_id);

          // Add lab any subsections
          for(const lab_subsection_id of lab_subsections)
          {
            if(!results.includes(lab_subsection_id)) results.push(lab_subsection_id);
          }

          return results;
        }, affected_section_ids);
      }
    }

    // Update BACKEND
    const data = {
      school_id: schoolInfo.school_id,
      schedule_version_id: currentScheduleVersionID,
      affected_sections: affected_section_ids.filter(section_id => {
        const section_info = sections.find(section => `${section.section_id}` === `${section_id}`);
        return sectionIsAllowedForCurrentUser(section_info);
      }),
      locked: locked
    };
    if (data.affected_sections.length === 0) {
      setIsLockSectionLoading(false);
      setIsLockSectionIds([]);
      return;
    }
    
    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = '/update-lock-section';
    const myInit = { // OPTIONAL
      response: true,
      body: JSON.stringify(data),
    };
    
    try {
      const response = await API.post(apiName, url, myInit);
      const returned_affected_section_ids = Array.isArray(response?.data?.affected_sections)
        ? response.data.affected_sections
        : affected_section_ids;
      returned_affected_section_ids.forEach(section_id => {
        const section_index = sections.findIndex(section => section.section_id === section_id);
        if(section_index !== -1) sections[section_index].locked = locked;
      });
      setSections([...sections]);
      setIsLockSectionLoading(false);
      setIsLockSectionIds([]);
    } catch(e) {
      setIsLockSectionLoading(false);
      setIsLockSectionIds([]);
      console.log(e.response);
    }
  }

  const lockCurrentSections = async () => {
    const options =  {
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Lock Visible Sections",
          value: true,
          visible: true,
          className: 'bright-yellow-btn',
          closeModal: false
        },
      },
      content: (
        <div>
          <FontAwesomeIcon className='modal-top-icon bright-yellow-text' icon={faLock}/>
          <h1 className='swal-title' style={{marginTop:'0px'}}>Lock Visible Sections</h1>
          <p className='swal-text'>This will lock all sections that are currently visible on the Magnetboard. Please add any filters (until only the sections you want are shown) before running this function!</p>
        </div>
        
      )
    }

    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        const locking_in_progress =  {
          button:false,
          closeOnClickOutside: false,
          closeOnEsc: false,
          content: (
            <div>
              <FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faLock}/>
              <h1 className='swal-title' style={{marginTop:'0px'}}>Locking Sections in Progress</h1>
              <div className='loader'></div>
            </div>
          )
        }

        swal(locking_in_progress);

        setTimeout(async () => {
          const visible_sections = getCurrentVisibleSectionIDs().filter(section_id => {
            const temp_section_info = sections.find(temp_section => temp_section.section_id === section_id);
            return temp_section_info && temp_section_info.locked !== '1';
          });
          await toggleLockSection(visible_sections, '1');
          swal.close();
        }, 100);
      }
    });
  }

  const unlockCurrentSections = async () => {
    const options =  {
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: 'gray-btn'
        },
        confirm: {
          text: "Unlock Visible Sections",
          value: true,
          visible: true,
          className: 'blue-btn',
          closeModal: false
        },
      },
      content: (
        <div>
          <FontAwesomeIcon className='modal-top-icon blue-text' icon={faUnlock}/>
          <h1 className='swal-title' style={{marginTop:'0px'}}>Unlock Visible Sections</h1>
          <p className='swal-text'>This will unlock all sections that are currently visible on the Magnetboard. Please add any filters (until only the sections you want are shown) before running this function!</p>
        </div>
        
      )
    }

    await swal(options).then(async (return_value) => {
      if(return_value)
      {
        const locking_in_progress =  {
          button:false,
          closeOnClickOutside: false,
          closeOnEsc: false,
          content: (
            <div>
              <FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faUnlock}/>
              <h1 className='swal-title' style={{marginTop:'0px'}}>Unlocking Sections in Progress</h1>
              <div className='loader'></div>
            </div>
          )
        }

        swal(locking_in_progress);

        setTimeout(async () => {
          const visible_sections = getCurrentVisibleSectionIDs().filter(section_id => {
            const temp_section_info = sections.find(temp_section => temp_section.section_id === section_id);
            return temp_section_info && temp_section_info.locked === '1';
          });
          await toggleLockSection(visible_sections, '0');
          swal.close();
        }, 100);
      }
    });
  }

  const getCurrentVisibleSectionIDs = useCallback(() => {
    const visibleDataIDs = new Set(filteredDisplaySchedule.map((entity) => entity.data_id));
    const sectionIsInVisiblePeriod = (section) =>
      Array.isArray(section.course_periods) &&
      section.course_periods.some((course_period_id) => visibleCoursePeriodIDs.has(`${course_period_id}`));

    const target_sections = sections.reduce((results, section) => {
      const section_id = section.section_id;
      if (
        !section_id ||
        !sectionIsInVisiblePeriod(section) ||
        !sectionIsAllowedForCurrentUser(section) ||
        (currentView === "course" && !sectionMatchesCourseViewPeriodTermFilters(section, filterOptions)) ||
        !sectionPassesSectionFilters({
          section,
          sectionSingletonDoubletonMap,
          labsOnlyFiltered: labs_only_filtered,
          nonLabsOnlyFiltered: non_labs_only_filtered,
          singletonsFiltered: singletons_filtered,
          doubletonsFiltered: doubletons_filtered,
          hasClassroomFiltered: has_classroom_filtered,
          noClassroomFiltered: no_classroom_filtered,
        })
      ) {
        return results;
      }

      if (currentView === "teacher") {
        const isMainTeacherVisible = visibleDataIDs.has(section.teacher_id);
        const hasVisibleSecondary =
          Array.isArray(section.secondary_teachers) &&
          section.secondary_teachers.some((teacher_id) => visibleDataIDs.has(teacher_id));
        if (isMainTeacherVisible || hasVisibleSecondary) results.push(section_id);
      } else if (currentView === "course") {
        if (visibleDataIDs.has(section.course_id)) results.push(section_id);
      } else if (currentView === "classroom") {
        if (visibleDataIDs.has(section.classroom_id)) results.push(section_id);
      }

      return results;
    }, []);

    return [...new Set(target_sections)];
  }, [
    currentView,
    filteredDisplaySchedule,
    filterOptions,
    has_classroom_filtered,
    labs_only_filtered,
    no_classroom_filtered,
    non_labs_only_filtered,
    sections,
    sectionIsAllowedForCurrentUser,
    sectionSingletonDoubletonMap,
    singletons_filtered,
    doubletons_filtered,
    visibleCoursePeriodIDs,
  ]);

	  const toggleEditSection = useCallback((passed_data) => {
			let { section_id = null, data_id = null, section_type = null, course_period_id = null } = passed_data || {};

		// Check if section is subsection
		if(section_id)
		{
			const subsection = subsections.find(subsection => subsection.subsection_id === section_id);
			if(subsection)
			{
				section_id = subsection.section_id;
			}

      const section_info = sections.find(section => `${section.section_id}` === `${section_id}`);
      if(!sectionIsAllowedForCurrentUser(section_info)) return;
		}

    const editSectionInfo = {
      section_id: section_id,
      data_id: data_id,
      section_type: section_type,
      course_period_id: course_period_id
    }

	    setEditSectionModalInfo(editSectionInfo);
	    toggleShowEditSectionModal();
		}, [subsections, sections, sectionIsAllowedForCurrentUser, setEditSectionModalInfo, toggleShowEditSectionModal]);

  const showSectionActionDeniedModal = useCallback((action, message = null) => {
    const action_display = action || "update";
    swal({
      title: `Unable to ${action_display}`,
      icon: "warning",
      buttons: {
        cancel: {
          text: "Okay",
          value: false,
          visible: true,
          className: "blue-btn",
        },
      },
      content: (
        <div>
          <p>{message || "You do not have permission to update this section."}</p>
        </div>
      ),
    });
  }, []);

	  const duplicateSectionFromMagnetboard = useCallback(async (sectionID, options = {}) => {
	    if (!sectionID) return;
	    const { copyPeriods = true } = options;
	    const section_info = sections.find(section => `${section.section_id}` === `${sectionID}`);
	    if (!sectionIsAllowedForCurrentUser(section_info)) {
	      showSectionActionDeniedModal("Duplicate", "You do not have permission to duplicate this section.");
	      return;
	    }

    setConflictsLoading(true);
    setMatchPercentLoading(true);

    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = '/admin/duplicate-section';
    const myInit = {
      response: true,
      body: JSON.stringify({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        section_id: sectionID,
        department_id: magnetboardOptions.current_department_id,
        subscreen: magnetboardOptions.progress_subscreen,
        copy_periods: copyPeriods,
      }),
    };

	    try {
	      const response = await API.post(apiName, url, myInit);
	      const response_data = response?.data || response;
	      if (
	        response_data &&
	        (response_data.success === false ||
	          response_data.status === "error" ||
	          response_data.msg ||
	          response_data.message)
	      ) {
	        throw new Error(response_data.msg || response_data.message || "There was a problem duplicating this section.");
	      }

	      const sections_to_return = Array.isArray(response_data?.sections_to_return)
	        ? response_data.sections_to_return
	        : [];
	      const subsections_to_return = Array.isArray(response_data?.subsections_to_return)
	        ? response_data.subsections_to_return
	        : [];

      if (sections_to_return.length > 0) {
        setSections((current_sections) => [...current_sections, ...sections_to_return]);
      }

      if (subsections_to_return.length > 0) {
        setSubsections((current_subsections) => [...current_subsections, ...subsections_to_return]);
      }

      notifyPlacementChangeSaved({change_type: "duplicate_section", section_id: sectionID});

      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        match_percent: true,
      }).then((conflicts_response) => {
        setConflicts([...(conflicts_response?.conflicts || [])]);
        setMatchPercent(conflicts_response?.match_percent || 0);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      }).catch((error) => {
        console.log(error?.response || error);
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      });
    } catch (error) {
      swal({
        title: "Unable to Duplicate",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Okay",
            value: false,
            visible: true,
            className: "blue-btn",
          },
        },
	        content: (
	          <div>
	            <p>{error?.response?.data?.msg || error?.response?.data?.message || error?.message || "There was a problem duplicating this section."}</p>
	          </div>
	        ),
      });
      setConflictsLoading(false);
      setMatchPercentLoading(false);
    }
  }, [
    schoolInfo.school_id,
    schoolInfo.current_schedule_version_id,
    markConflictDataFresh,
    magnetboardOptions.current_department_id,
    magnetboardOptions.progress_subscreen,
    notifyPlacementChangeSaved,
	    sections,
	    sectionIsAllowedForCurrentUser,
	    showSectionActionDeniedModal,
	    setConflicts,
    setConflictsLoading,
    setMatchPercent,
    setMatchPercentLoading,
    setSections,
    setSubsections,
  ]);

  const setDuplicateLoadingTarget = useCallback((dataID, coursePeriodID, isLoading) => {
    const loading_key = `${dataID || ""}::${coursePeriodID || ""}`;

    setDuplicateLoadingTargets((current_targets) => {
      const current_count = current_targets[loading_key] || 0;
      if (isLoading) {
        return {
          ...current_targets,
          [loading_key]: current_count + 1,
        };
      }

      if (current_count <= 1) {
        const { [loading_key]: _removed, ...remaining_targets } = current_targets;
        return remaining_targets;
      }

      return {
        ...current_targets,
        [loading_key]: current_count - 1,
      };
    });
  }, []);

  const setDeleteLoadingTarget = useCallback((sectionIDs, isLoading) => {
    const normalized_section_ids = (Array.isArray(sectionIDs) ? sectionIDs : [sectionIDs])
      .map((section_id) => `${section_id || ""}`)
      .filter(Boolean);
    if (normalized_section_ids.length === 0) return;

    setDeleteLoadingTargets((current_targets) => {
      const next_targets = { ...current_targets };

      normalized_section_ids.forEach((section_id) => {
        const current_count = next_targets[section_id] || 0;
        if (isLoading) {
          next_targets[section_id] = current_count + 1;
        } else if (current_count <= 1) {
          delete next_targets[section_id];
        } else {
          next_targets[section_id] = current_count - 1;
        }
      });

      return next_targets;
    });
  }, []);

	  const deleteSectionFromMagnetboard = useCallback(async (sectionID) => {
	    if (!sectionID) return;
	    const section_info = sections.find(section => `${section.section_id}` === `${sectionID}`);
	    if (!sectionIsAllowedForCurrentUser(section_info)) {
	      showSectionActionDeniedModal("Delete", "You do not have permission to delete this section.");
	      return;
	    }

    setConflictsLoading(true);
    setMatchPercentLoading(true);

    try {
      const apiName = process.env.REACT_APP_ENDPOINT_NAME;
      const response = await API.post(apiName, '/admin/delete-section', {
        response: true,
        body: JSON.stringify({
          school_id: schoolInfo.school_id,
          schedule_version_id: schoolInfo.current_schedule_version_id,
          section_id: sectionID,
          department_id: magnetboardOptions.current_department_id,
          subscreen: magnetboardOptions.progress_subscreen,
        }),
	      });
	      const response_data = response?.data || response;
	      if (
	        response_data &&
	        (response_data.success === false ||
	          response_data.status === "error" ||
	          response_data.msg ||
	          response_data.message)
	      ) {
	        throw new Error(response_data.msg || response_data.message || "There was a problem deleting this section.");
	      }

	      const deleted_sections = Array.isArray(response_data?.deleted_sections)
        ? response_data.deleted_sections.map((deleted_section_id) => `${deleted_section_id}`)
        : [];
      const deleted_sections_set = new Set(deleted_sections);

      if (deleted_sections.length > 0) {
        setSections((current_sections) =>
          current_sections.filter((section) => !deleted_sections_set.has(`${section.section_id}`))
        );
        setSubsections((current_subsections) =>
          current_subsections.filter(
            (subsection) =>
              !deleted_sections_set.has(`${subsection.section_id}`) &&
              !deleted_sections_set.has(`${subsection.subsection_id}`)
          )
        );
      }

      notifyPlacementChangeSaved({change_type: "delete_section", section_id: sectionID});

      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        match_percent: true,
      }).then((conflicts_response) => {
        setConflicts([...(conflicts_response?.conflicts || [])]);
        setMatchPercent(conflicts_response?.match_percent || 0);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      }).catch((error) => {
        console.log(error?.response || error);
        setConflictsLoading(false);
        setMatchPercentLoading(false);
      });
    } catch (error) {
      swal({
        title: "Unable to Delete",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Okay",
            value: false,
            visible: true,
            className: "blue-btn",
          },
        },
	        content: (
	          <div>
	            <p>{error?.response?.data?.msg || error?.response?.data?.message || error?.message || "There was a problem deleting this section."}</p>
	          </div>
	        ),
      });
    } finally {
      setConflictsLoading(false);
      setMatchPercentLoading(false);
    }
  }, [
    schoolInfo.school_id,
    schoolInfo.current_schedule_version_id,
    markConflictDataFresh,
    magnetboardOptions.current_department_id,
    magnetboardOptions.progress_subscreen,
    notifyPlacementChangeSaved,
	    sections,
	    sectionIsAllowedForCurrentUser,
	    showSectionActionDeniedModal,
	    setConflicts,
    setConflictsLoading,
    setMatchPercent,
    setMatchPercentLoading,
    setSections,
    setSubsections,
  ]);

  const toggleEditStudentSchedule = useCallback((studentId, navigationStudentIDs = null) => {
    const has_student_id = studentId !== null && studentId !== undefined;
    const student_navigation_ids = has_student_id
      ? (Array.isArray(navigationStudentIDs)
        ? navigationStudentIDs
        : (currentView === "student" ? filteredStudentNavigationIDs : null))
      : null;

    setEditStudentScheduleModalInfo({
      student_id: studentId,
      student_navigation_ids,
    });

    if (!studentId) {
      setEditStudentScheduleModal(false);
    } else if (showEditStudentScheduleModal === false) {
      setEditStudentScheduleModal(true);
    }
  }, [currentView, filteredStudentNavigationIDs, setEditStudentScheduleModalInfo, setEditStudentScheduleModal, showEditStudentScheduleModal]);

  const toggleEditData = useCallback((data_type = null, data_id = null) => {
    if(!data_type || data_id === null || data_id === undefined)
    {
      setEditDataModalInfo(null);
      return;
    }
    if(magnetboardOptions.allow_hotlinks === false) return;

    setEditDataModalInfo({data_type, data_id});
  }, [magnetboardOptions.allow_hotlinks]);

  const togglePeriodAvailability = useCallback(async ({data_type, data_id, course_period_id, is_restricted}) => {
    if(!['teacher', 'course'].includes(data_type) || !data_id || !course_period_id) return false;
    if(magnetboardOptions.allow_period_availability_editing === false) return false;

    const data_list = data_type === 'teacher' ? teachers : courses;
    const id_key = data_type === 'teacher' ? 'teacher_id' : 'course_id';
    const entity_info = data_list.find(data_info => `${data_info[id_key]}` === `${data_id}`);
    const entity_department_ids = Array.isArray(entity_info?.departments) ? entity_info.departments : [];
    const allowed_department_ids = new Set((magnetboardOptions.allowed_department_ids || []).map(department_id => `${department_id}`));
    const department_id = magnetboardOptions.current_department_id ||
      entity_department_ids.find(department_id => allowed_department_ids.size === 0 || allowed_department_ids.has(`${department_id}`)) ||
      (Array.isArray(magnetboardOptions.allowed_department_ids) ? magnetboardOptions.allowed_department_ids[0] : null) ||
      null;
    const ids_key = data_type === 'teacher' ? 'teacher_ids' : 'course_ids';
    const endpoint = data_type === 'teacher'
      ? '/admin/clean-data/update-teacher-availability'
      : '/admin/clean-data/update-course-availability';
    const request_data = {
      school_id: schoolInfo.school_id,
      schedule_version_id: schoolInfo.current_schedule_version_id,
      department_id,
      [ids_key]: [data_id],
      course_period_ids: [course_period_id],
      delete_restriction: is_restricted ? '1' : '0',
    };

    try
    {
      await API.post(process.env.REACT_APP_ENDPOINT_NAME, endpoint, {
        response: true,
        body: JSON.stringify(request_data),
      });

      const update_data_list = current_data_list => current_data_list.map(data_info => {
        if(`${data_info[id_key]}` !== `${data_id}`) return data_info;

        const current_restrictions = Array.isArray(data_info.period_restrictions) ? data_info.period_restrictions : [];
        const next_restrictions = is_restricted
          ? current_restrictions.filter(restricted_period_id => `${restricted_period_id}` !== `${course_period_id}`)
          : current_restrictions.some(restricted_period_id => `${restricted_period_id}` === `${course_period_id}`)
            ? current_restrictions
            : [...current_restrictions, course_period_id];

        return {...data_info, period_restrictions:next_restrictions};
      });

      if(data_type === 'teacher') setTeachers(update_data_list);
      else setCourses(update_data_list);

      const conflicts_to_check = [
        data_type === 'teacher'
          ? CONFLICT_TYPES.TEACHER_PERIOD_RESTRICTION
          : CONFLICT_TYPES.COURSE_PERIOD_RESTRICTION
      ];

      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        conflicts_to_check,
      }).then(conflicts_response => {
        const conflicts_list = Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : [];
        const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
        setConflicts([...filtered_out_conflicts, ...conflicts_list]);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
      }).catch(error => {
        console.log(error?.response || error);
      });

      return true;
    }
    catch(error)
    {
      swal({
        title: 'Unable to Update Availability',
        text: error?.response?.data?.message || error?.message || 'The period availability could not be updated. Please try again.',
        icon: 'error',
      });
      return false;
    }
  }, [
    conflicts,
    courses,
    magnetboardOptions.allow_period_availability_editing,
    magnetboardOptions.allowed_department_ids,
    magnetboardOptions.current_department_id,
    markConflictDataFresh,
    schoolInfo.current_schedule_version_id,
    schoolInfo.school_id,
    setConflicts,
    setCourses,
    setTeachers,
    teachers,
  ]);

  return (
    <>
      {showExportScheduleModal && (
        <Suspense fallback={null}>
          <ExportScheduleModal
            schoolInfo={schoolInfo}
            scheduleInfo={scheduleInfo}
            scheduleStructureData={scheduleStructureData}
            setScheduleStructureData={setScheduleStructureData}
            lunchInfo={lunchInfo}
            classrooms={classrooms}
            courses={courses}
            setCourses={setCourses}
            teachers={teachers}
            setTeachers={setTeachers}
            students={students}
            setStudents={setStudents}
            sections={sections}
            setSections={setSections}
            subsections={subsections}
            labels={labels}
            conflicts={conflicts}
            openConflicts={() => addOpenReport("conflicts")}
            closeModal={() => setShowExportScheduleModal(false)}
          />
        </Suspense>
      )}

      {editDataModalInfo && (
        <Suspense fallback={null}>
          {editDataModalInfo.data_type === 'teacher' && (
            <EditTeacherData
              dataID={editDataModalInfo.data_id}
              toggleEditData={toggleEditData}
              schoolInfo={schoolInfo}
              scheduleStructureData={scheduleStructureData}
              scheduleInfo={scheduleInfo}
              teachers={teachers}
              students={students}
              courses={courses}
              classrooms={classrooms}
              departments={departments}
              sections={sections}
              setDatabaseData={setTeachers}
            />
          )}
          {editDataModalInfo.data_type === 'course' && (
            <EditCourseData
              dataID={editDataModalInfo.data_id}
              toggleEditData={toggleEditData}
              schoolInfo={schoolInfo}
              scheduleStructureData={scheduleStructureData}
              scheduleInfo={scheduleInfo}
              teachers={teachers}
              students={students}
              courses={courses}
              classrooms={classrooms}
              departments={departments}
              sections={sections}
              setSections={setSections}
              labels={labels}
              setDatabaseData={setCourses}
              setStudents={setStudents}
            />
          )}
          {editDataModalInfo.data_type === 'classroom' && (
            <EditClassroomData
              dataID={editDataModalInfo.data_id}
              toggleEditData={toggleEditData}
              schoolInfo={schoolInfo}
              scheduleStructureData={scheduleStructureData}
              scheduleInfo={scheduleInfo}
              classrooms={classrooms}
              courses={courses}
              teachers={teachers}
              sections={sections}
              departments={departments}
              setDatabaseData={setClassrooms}
            />
          )}
          {editDataModalInfo.data_type === 'student' && (
            <EditStudentData
              dataID={editDataModalInfo.data_id}
              toggleEditData={toggleEditData}
              schoolInfo={schoolInfo}
              scheduleInfo={scheduleInfo}
              students={students}
              courses={courses}
              teachers={teachers}
              departments={departments}
              sections={sections}
              labels={labels}
              setDatabaseData={setStudents}
            />
          )}
        </Suspense>
      )}

      <div className="mb-container">
        <div className="mb-top-bar">
          <div className="mb-top-bar-inner">
            {magnetboardOptions.show_top_bar && (
              <MagnetboardTopBar
                magnetboardOptions={magnetboardOptions}
                scheduleData={scheduleTopBarMemo}
                visibleConflicts={scopedConflictsForReports}
                magnetboardActions={{
                  pullNewSectionData: pullNewSectionData,
                  handleUpdateScheduleVersionName,
                }}
              />
            )}
            {magnetboardOptions.show_menu && (
              <MagnetboardMenu
                magnetboardOptions={magnetboardOptions}
                scheduleData={scheduleMenuMemo}
                magnetboardActions={{
                  pullNewSectionData,
                  duplicateSchedule,
                  deleteSchedule,
                  undoAction,
                  redoAction,
                  clearStudents,
                  placeStudents,
                  clearClassrooms,
                  placeClassrooms,
                  clearSections,
                  placeSections,
                  lockCurrentSections,
                  unlockCurrentSections,
                  requestPrintSchedule,
                  requestExportCurrentView,
                }}
              />
            )}
            <MagnetboardFilters
              magnetboardOptions={magnetboardOptions}
              scheduleData={scheduleFiltersMemo}
              viewPreferencesLoaded={viewPreferencesLoaded}
              pendingViewSelection={pendingViewSelection}
              deferStudentViewWork={deferStudentViewWork}
              onChangeView={handleChangeViewRequest}
            />
          </div>
        </div>

        {studentViewTransitionActive && (
          <div className="mb-view-transition-overlay">
            <div className="mb-view-transition-card">
              <Loader size="56px" />
              <div className="mb-view-transition-title">Preparing Student View</div>
              <div className="mb-view-transition-subtitle">Loading students while keeping your current board in place.</div>
            </div>
          </div>
        )}

        {/* Panel */}
        <MagnetboardPanel
          magnetboardOptions={magnetboardOptions}
          scheduleData={schedulePanelMemo}
          magnetboardActions={{
            updateSectionPlacement,
            updateManualPlacement,
            toggleLockSection,
            sectionIsAllowedForCurrentUser,
            toggleEditSection,
            duplicateSectionFromMagnetboard,
            deleteSectionFromMagnetboard,
            setDuplicateLoadingTarget,
            duplicateLoadingTargets,
            setDeleteLoadingTarget,
            deleteLoadingTargets,
            toggleEditStudentSchedule,
            toggleEditData,
            togglePeriodAvailability,
          }}
        />
      </div>

      {printOptionsModalVisible && (
        <MagnetboardPrintOptionsModal
          currentView={currentView}
          rowCount={filteredDisplaySchedule.length}
          searchValue={searchValue}
          onClose={() => {
            setPrintOptionsModalVisible(false);
          }}
          onPrint={(print_request_options) => requestPrintSchedule("schedule", print_request_options)}
        />
      )}

      {excelExportOptionsModalVisible && (
        <MagnetboardExcelExportOptionsModal
          currentView={currentView}
          rowCount={filteredDisplaySchedule.length}
          onClose={() => setExcelExportOptionsModalVisible(false)}
          onExport={requestExportCurrentView}
        />
      )}

      {activePrintMode === "schedule" && (
        <MagnetboardPrintSchedule
          filteredDisplaySchedule={activePrintFilteredDisplaySchedule}
          printOptions={activePrintOptions}
          scheduleData={{
            scheduleStructureData,
            sections,
            setSections,
            subsections,
            departments,
            schoolInfo,
            scheduleInfo,
            currentScheduleVersionID,
            scheduleType,
            currentView,
            currentViewType,
            currentDay,
            periodsToShow,
            teachers,
            courses,
            classrooms,
            labels,
            labs_only_filtered,
            non_labs_only_filtered,
            singletons_filtered,
            doubletons_filtered,
            no_classroom_filtered,
            has_classroom_filtered,
            studentSectionsByStudentCoursePeriod,
            studentConflictSectionLookup,
            sectionConflictSummaryBySectionId,
            sectionsById,
            subsectionParentById,
            teachersById,
            coursesById,
            classroomsById,
            labelsById,
          }}
        />
      )}
      {activePrintMode === "by_teacher" && currentView === "teacher" && (
        <MagnetboardPrintByTeacher
          filteredDisplaySchedule={filteredDisplaySchedule}
          scheduleData={{
            scheduleStructureData,
            sections,
            setSections,
            subsections,
            departments,
            schoolInfo,
            scheduleInfo,
            currentScheduleVersionID,
            scheduleType,
            currentViewType,
            currentDay,
            periodsToShow,
            teachers,
            courses,
            classrooms,
            labels,
            labs_only_filtered,
            non_labs_only_filtered,
            singletons_filtered,
            doubletons_filtered,
            no_classroom_filtered,
            has_classroom_filtered,
            sectionsById,
            subsectionParentById,
            labelsById,
          }}
        />
      )}
    </>
  );
}
